import { Injectable } from '@angular/core';


interface FieldData {
  isVisible: boolean;
  isEditable?: boolean;
  isMandatory?: boolean;
  fieldType?: string;
  fieldData?: {};
  label?: string;
  tooltip?: string;
  conditions?: {
    [conditionKey: string]: {
      fieldData?: {
        [fieldKey: string]: FieldData;
      }
      comparison?: string;
      comparisonSymbol?: string;
      fieldsAppliedTo?: string[];
    };
  };
}

interface SectionConfig {
  isVisible: boolean;
  isEditable?: boolean;
  isMandatory?: boolean;
  fieldType?: string;
  fieldData?: {
    [fieldKey: string]: ProductConfig | FieldData;
  };
  label?: string;
}

export interface ProductConfig {
  [productCategory: string]: {
    [productType: string]: {
      [sectionKey: string]: SectionConfig;
    };
  };
}

export interface CreditFacilityConfig {
  [productType: string]: {
    commonConfig: {
      isBuyer: boolean,
      isSeller: boolean
      allProductTypeCategoryFinanceCombos: string[],
      isCategoryEnabled: boolean
    },
    applyConfig: {
      captureLimitRequired: boolean, //boolean
      restrictDisbursalKeyName: string, //restrict_disbursal_ + shortFormOfProductType
      assignmentOfReceivablesApplicable: boolean, //boolean
      counterPartyAcceptanceRequired: boolean //boolean,
      bankDetails: boolean,
      restrictForFI: boolean,
      isRiskObligorSelf: boolean
    },
    counterPartyComponentsConfig: {
      counterPartyFormType: string, //compact / full,
      fullFormTypeAllowed: boolean,
      apsCounterPartyFormType: string,
    },
    approveConfig: {
      captureLimitRequired: boolean,
      restrictDisbursalKeyName: string,
      termsheetRequired: boolean,
      assignmentOfReceivablesApplicable: boolean,
      allowCPDeletion: boolean,
      supplierAgreement: boolean
    },
    creditLimitTermsConfig: {
      minCreditPeriodVisible: boolean,
      maxFinancingTenureOptionsProductCategoryList: string[],
      importerAcceptanceValidatorRequired: boolean,
      factoringFee: boolean,
      advanceFacilityConditions: AdvanceFacilityConditions, // (true will be treated as Yes and false as No)
      interestRateConfigurable: string,
      feeAndChargesCondition: FeeAndChargesCondition,
    }
  };
}

export enum InterestRateConfigurableType {
  CREDITPOLICY = 'creditPolicy',
  FLEXIBLE = 'flexible',
}

export interface FeeAndChargesCondition {
  collectedAt: CollectedAt,
  collectedFrom: CollectedFrom,
}

export enum CollectedAt {
  UPFRONT = 'upfront',
  ONSETTLEMENT = 'onsettlement',
  FLEXIBLE = 'flexible',
  NA = 'na'
}

export enum CollectedFrom {
  APPLICANT = 'applicant',
  COUNTERPARTY = 'counterparty',
  FLEXIBLE = 'flexible'
}
export interface AdvanceFacilityConditions {
  riskParticipationValue: boolean,
  riskParticipationType: boolean,
  riskParticipant: boolean,
  riskOriginator: boolean,
  participationType: boolean,
}

export enum ProductCategory {
  Trade,
  Logistics
}

export enum ProductType {
  invoiceDiscounting,
  invoiceFactoring,
  reverseFactoring,
  tradePayablesFinancing,
  invoiceFactoringWoRecourse,
  inventoryFinanceBuyer,
  inventoryFinanceSeller,
  revolvingCreditFacility,
  reverseFactoringSeller,
  mrpa,
  mrpaClient,
  scfDpwfs,
  advancePaymentSolutionClient,
  advancePaymentSolutionCounterParty
}

export interface FinanceTypeInterface {
  value: string;
  viewValue: string;
}

export interface FinanceTypeInfoInterface {
  productType: FinanceTypeInterface;
  financeType?: FinanceTypeInterface;
  productCategory?: FinanceTypeInterface;
}

export interface MawaniAllowedFinTypes {
  logistics_finance_invoice_discounting: boolean;
  logistics_finance_invoice_factoring: boolean;
  logistics_finance_invoice_factoring_wo_recourse: boolean;
  logistics_finance_reverse_factoring: boolean;
  logistics_finance_trade_payables_financing: boolean;
  trade_finance_invoice_discounting: boolean;
  trade_finance_invoice_factoring: boolean;
  trade_finance_invoice_factoring_wo_recourse: boolean;
  trade_finance_inventory_finance_buyer: boolean;
  trade_finance_inventory_finance_seller: boolean;
  trade_finance_revolving_credit_facility: boolean;
  trade_finance_reverse_factoring: boolean;
  trade_finance_trade_payables_financing: boolean;
  trade_finance_reverse_factoring_seller: boolean;
  trade_finance_mrpa: boolean;
  trade_finance_mrpa_client: boolean;
  trade_finance_scf_dpwfs: boolean;
  trade_finance_advance_payment_solution_client: boolean;
  trade_finance_advance_payment_solution_cp: boolean;
  supply_chain_financing: boolean;
  logistics_finance_advance_payment_solution_client: boolean;
  logistics_finance_advance_payment_solution_cp: boolean;
}

export class Finances {

  public static productType = [
    { value: '1', viewValue: 'Invoice Discounting' },
    { value: '2', viewValue: 'Invoice Factoring' },
    { value: '3', viewValue: 'Reverse Factoring' },
    { value: '4', viewValue: 'Trade Payables Financing' },
    { value: '5', viewValue: 'Invoice Factoring w/o Recourse' },
    { value: '6', viewValue: 'Inventory Finance Buyer' },
    { value: '7', viewValue: 'Inventory Finance Seller' },
    { value: '8', viewValue: 'Revolving Credit Facility' },
    { value: '9', viewValue: 'Reverse Factoring - Charges Borne by Seller' },
    { value: '10', viewValue: 'MRPA' },
    { value: '11', viewValue: 'MRPA - Disbursement to Client' },
    { value: '12', viewValue: 'SCF by DPWFS' },
    { value: '13', viewValue: 'Advance Payment Solution – Disbursement to Client' },
    { value: '14', viewValue: 'Advance Payment Solution – Disbursement to Counter Party' },
  ];

  public static productCategory = [
    { value: '1', viewValue: 'Trade Finance' },
    { value: '2', viewValue: 'Logistics Finance' }
  ];

  public static financeType = [
    { value: '1', viewValue: 'DEFERRED' },
    { value: '2', viewValue: 'IN_TRANSIT' }
  ];

  public static financeProductType = [
    { value: '1', viewValue: 'Trade Finance' },
    { value: '2', viewValue: 'Logistics Finance' }
  ]
}

export enum LOGISTICS_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'INVOICE_DISCOUNTING',
  financeType = 'IN_TRANSIT'
}
export enum LOGISTICS_FINANCE_INVOICE_DISCOUNTING_DEFERRED {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'INVOICE_DISCOUNTING',
  financeType = 'DEFERRED'
}
export enum LOGISTICS_FINANCE_INVOICE_FACTORING_IN_TRANSIT {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'INVOICE_FACTORING',
  financeType = 'IN_TRANSIT'
}
export enum LOGISTICS_FINANCE_INVOICE_FACTORING_DEFERRED {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'INVOICE_FACTORING',
  financeType = 'DEFERRED'
}
export enum LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'INVOICE_FACTORING_WO_RECOURSE',
  financeType = 'IN_TRANSIT'
}
export enum LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'INVOICE_FACTORING_WO_RECOURSE',
  financeType = 'DEFERRED'
}
export enum LOGISTICS_FINANCE_REVERSE_FACTORING_IN_TRANSIT {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'REVERSE_FACTORING',
  financeType = 'IN_TRANSIT'
}
export enum LOGISTICS_FINANCE_REVERSE_FACTORING_DEFERRED {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'REVERSE_FACTORING',
  financeType = 'DEFERRED'
}
export enum LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'TRADE_PAYABLES_FINANCING',
  financeType = 'IN_TRANSIT'
}
export enum LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'TRADE_PAYABLES_FINANCING',
  financeType = 'DEFERRED'
}

export enum LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'ADVANCE_PAYMENT_SOLUTION_CLIENT',
  financeType = 'IN_TRANSIT'
}
export enum LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'ADVANCE_PAYMENT_SOLUTION_CLIENT',
  financeType = 'DEFERRED'
}
export enum LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'ADVANCE_PAYMENT_SOLUTION_CP',
  financeType = 'IN_TRANSIT'
}
export enum LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED {
  productCategory = 'LOGISTICS_FINANCE',
  productType = 'ADVANCE_PAYMENT_SOLUTION_CP',
  financeType = 'DEFERRED'
}

export enum TRADE_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT {
  productCategory = 'TRADE_FINANCE',
  productType = 'INVOICE_DISCOUNTING',
  financeType = 'IN_TRANSIT'
}
export enum TRADE_FINANCE_INVOICE_DISCOUNTING_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'INVOICE_DISCOUNTING',
  financeType = 'DEFERRED'
}
export enum TRADE_FINANCE_INVOICE_FACTORING_IN_TRANSIT {
  productCategory = 'TRADE_FINANCE',
  productType = 'INVOICE_FACTORING',
  financeType = 'IN_TRANSIT'
}
export enum TRADE_FINANCE_INVOICE_FACTORING_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'INVOICE_FACTORING',
  financeType = 'DEFERRED'
}
export enum TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT {
  productCategory = 'TRADE_FINANCE',
  productType = 'INVOICE_FACTORING_WO_RECOURSE',
  financeType = 'IN_TRANSIT'
}
export enum TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'INVOICE_FACTORING_WO_RECOURSE',
  financeType = 'DEFERRED'
}
export enum TRADE_FINANCE_REVERSE_FACTORING_IN_TRANSIT {
  productCategory = 'TRADE_FINANCE',
  productType = 'REVERSE_FACTORING',
  financeType = 'IN_TRANSIT'
}
export enum TRADE_FINANCE_REVERSE_FACTORING_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'REVERSE_FACTORING',
  financeType = 'DEFERRED'
}
export enum TRADE_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT {
  productCategory = 'TRADE_FINANCE',
  productType = 'TRADE_PAYABLES_FINANCING',
  financeType = 'IN_TRANSIT'
}
export enum TRADE_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'TRADE_PAYABLES_FINANCING',
  financeType = 'DEFERRED'
}

export enum TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT {
  productCategory = 'TRADE_FINANCE',
  productType = 'ADVANCE_PAYMENT_SOLUTION_CLIENT',
  financeType = 'IN_TRANSIT'
}
export enum TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'ADVANCE_PAYMENT_SOLUTION_CLIENT',
  financeType = 'DEFERRED'
}
export enum TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT {
  productCategory = 'TRADE_FINANCE',
  productType = 'ADVANCE_PAYMENT_SOLUTION_CP',
  financeType = 'IN_TRANSIT'
}
export enum TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'ADVANCE_PAYMENT_SOLUTION_CP',
  financeType = 'DEFERRED'
}

export enum TRADE_FINANCE_INVENTORY_FINANCE_BUYER_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'INVENTORY_FINANCE_BUYER',
  financeType = 'DEFERRED'
}

export enum TRADE_FINANCE_INVENTORY_FINANCE_SELLER_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'INVENTORY_FINANCE_SELLER',
  financeType = 'DEFERRED'
}

export enum TRADE_FINANCE_REVOLVING_CREDIT_FACILITY_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'REVOLVING_CREDIT_FACILITY',
  financeType = 'DEFERRED'
}

export enum TRADE_FINANCE_REVERSE_FACTORING_SELLER_IN_TRANSIT {
  productCategory = 'TRADE_FINANCE',
  productType = 'REVERSE_FACTORING_SELLER',
  financeType = 'IN_TRANSIT'
}

export enum TRADE_FINANCE_REVERSE_FACTORING_SELLER_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'REVERSE_FACTORING_SELLER',
  financeType = 'DEFERRED'
}

export enum TRADE_FINANCE_MRPA_IN_TRANSIT {
  productCategory = 'TRADE_FINANCE',
  productType = 'MRPA',
  financeType = 'IN_TRANSIT'
}

export enum TRADE_FINANCE_MRPA_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'MRPA',
  financeType = 'DEFERRED'
}

export enum TRADE_FINANCE_MRPA_CLIENT_IN_TRANSIT {
  productCategory = 'TRADE_FINANCE',
  productType = 'MRPA_CLIENT',
  financeType = 'IN_TRANSIT'
}

export enum TRADE_FINANCE_MRPA_CLIENT_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'MRPA_CLIENT',
  financeType = 'DEFERRED'
}

export enum TRADE_FINANCE_SCF_DPWFS_IN_TRANSIT {
  productCategory = 'TRADE_FINANCE',
  productType = 'SCF_DPWFS',
  financeType = 'IN_TRANSIT'
}

export enum TRADE_FINANCE_SCF_DPWFS_DEFERRED {
  productCategory = 'TRADE_FINANCE',
  productType = 'SCF_DPWFS',
  financeType = 'DEFERRED'
}

export const counterPartyFormTypeENUM = {
  FULL: 'full',
  COMPACT: 'compact',
  NOT_APPLICABLE: 'na'
}

export const PRODUCT_LIST = {
  LOGISTICS_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT: LOGISTICS_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT,
  LOGISTICS_FINANCE_INVOICE_DISCOUNTING_DEFERRED: LOGISTICS_FINANCE_INVOICE_DISCOUNTING_DEFERRED,
  LOGISTICS_FINANCE_INVOICE_FACTORING_IN_TRANSIT: LOGISTICS_FINANCE_INVOICE_FACTORING_IN_TRANSIT,
  LOGISTICS_FINANCE_INVOICE_FACTORING_DEFERRED: LOGISTICS_FINANCE_INVOICE_FACTORING_DEFERRED,
  LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT: LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT,
  LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED: LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED,
  LOGISTICS_FINANCE_REVERSE_FACTORING_IN_TRANSIT: LOGISTICS_FINANCE_REVERSE_FACTORING_IN_TRANSIT,
  LOGISTICS_FINANCE_REVERSE_FACTORING_DEFERRED: LOGISTICS_FINANCE_REVERSE_FACTORING_DEFERRED,
  LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT: LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT,
  LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED: LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED,
  LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT: LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT,
  LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED: LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED,
  LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT: LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT,
  LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED: LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED,

  TRADE_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT: TRADE_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT,
  TRADE_FINANCE_INVOICE_DISCOUNTING_DEFERRED: TRADE_FINANCE_INVOICE_DISCOUNTING_DEFERRED,
  TRADE_FINANCE_INVOICE_FACTORING_IN_TRANSIT: TRADE_FINANCE_INVOICE_FACTORING_IN_TRANSIT,
  TRADE_FINANCE_INVOICE_FACTORING_DEFERRED: TRADE_FINANCE_INVOICE_FACTORING_DEFERRED,
  TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT: TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT,
  TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED: TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED,
  TRADE_FINANCE_INVENTORY_FINANCE_BUYER_DEFERRED: TRADE_FINANCE_INVENTORY_FINANCE_BUYER_DEFERRED,
  TRADE_FINANCE_INVENTORY_FINANCE_SELLER_DEFERRED: TRADE_FINANCE_INVENTORY_FINANCE_SELLER_DEFERRED,
  TRADE_FINANCE_REVOLVING_CREDIT_FACILITY_DEFERRED: TRADE_FINANCE_REVOLVING_CREDIT_FACILITY_DEFERRED,
  TRADE_FINANCE_REVERSE_FACTORING_IN_TRANSIT: TRADE_FINANCE_REVERSE_FACTORING_IN_TRANSIT,
  TRADE_FINANCE_REVERSE_FACTORING_DEFERRED: TRADE_FINANCE_REVERSE_FACTORING_DEFERRED,
  TRADE_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT: TRADE_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT,
  TRADE_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED: TRADE_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED,
  TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT: TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT,
  TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED: TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED,
  TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT: TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT,
  TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED: TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED,
  TRADE_FINANCE_REVERSE_FACTORING_SELLER_IN_TRANSIT: TRADE_FINANCE_REVERSE_FACTORING_SELLER_IN_TRANSIT,
  TRADE_FINANCE_REVERSE_FACTORING_SELLER_DEFERRED: TRADE_FINANCE_REVERSE_FACTORING_SELLER_DEFERRED,
  TRADE_FINANCE_MRPA_IN_TRANSIT: TRADE_FINANCE_MRPA_IN_TRANSIT,
  TRADE_FINANCE_MRPA_DEFERRED: TRADE_FINANCE_MRPA_DEFERRED,
  TRADE_FINANCE_MRPA_CLIENT_IN_TRANSIT: TRADE_FINANCE_MRPA_CLIENT_IN_TRANSIT,
  TRADE_FINANCE_MRPA_CLIENT_DEFERRED: TRADE_FINANCE_MRPA_CLIENT_DEFERRED,
  TRADE_FINANCE_SCF_DPWFS_IN_TRANSIT: TRADE_FINANCE_SCF_DPWFS_IN_TRANSIT,
  TRADE_FINANCE_SCF_DPWFS_DEFERRED: TRADE_FINANCE_SCF_DPWFS_DEFERRED,
}

export const ALLOWED_ADVANCE_PAYMENT_SOLUTION_CLIENT_PRODUCTS = [
  'TRADE_FINANCE_RECEIVABLE_INVOICE_DISCOUNTING',
  'TRADE_FINANCE_RECEIVABLE_INVOICE_FACTORING',
  'INVENTORY_FINANCE',
  'INVOICE_DISCOUNTING',
  'INVOICE_FACTORING',
  'INVENTORY_FINANCE_SELLER'
];

export const ALLOWED_ADVANCE_PAYMENT_SOLUTION_COUNTERPARTY_PRODUCTS = [
  'TRADE_PAYABLES_FINANCING',
];

export enum CreditFacilityCounterPartyFormType {
  FULL = 'full',
  COMPACT = 'compact',
  NOT_APPLICABLE = 'na'
}

@Injectable({
  providedIn: 'root'
})
export class ProductConfigService {

  constructor() { }

  financingType = [
    { value: 'DEFERRED', viewValue: 'Deferred' },
    { value: 'IN_TRANSIT', viewValue: 'In-transit' }
  ];

  defaultProductType = ProductType.invoiceDiscounting;
  defaultProductCategory = ProductCategory.Trade;

  productOnboardingConfig = {
    products: [
      { apiKey: 'TRADE_FINANCE_INVOICE_DISCOUNTING', viewValue: 'Trade Finance: Invoice Discounting' },
      { apiKey: 'TRADE_FINANCE_INVOICE_FACTORING', viewValue: 'Trade Finance: Invoice Factoring' },
      { apiKey: 'TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE', viewValue: 'Trade Finance: Invoice Factoring w/o Recourse' },
      { apiKey: 'TRADE_FINANCE_INVENTORY_FINANCE_BUYER', viewValue: 'Inventory Finance Buyer' },
      { apiKey: 'TRADE_FINANCE_INVENTORY_FINANCE_SELLER', viewValue: 'Inventory Finance Seller' },
      { apiKey: 'TRADE_FINANCE_REVERSE_FACTORING', viewValue: 'Trade Finance: Reverse Factoring' },
      { apiKey: 'TRADE_FINANCE_REVOLVING_CREDIT_FACILITY', viewValue: 'Revolving Credit Facility' },
      { apiKey: 'TRADE_FINANCE_TRADE_PAYABLES_FINANCING', viewValue: 'Trade Finance: Trade Payables Financing' },
      { apiKey: 'TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP', viewValue: 'Trade Finance: Advance Payment Solution – Disbursement to Counter Party' },
      { apiKey: 'TRADE_FINANCE_REVERSE_FACTORING_SELLER', viewValue: 'Reverse Factoring - Charges Borne by Seller' },
      { apiKey: 'TRADE_FINANCE_MRPA', viewValue: 'MRPA' },
      { apiKey: 'TRADE_FINANCE_MRPA_CLIENT', viewValue: 'MRPA - Disbursement to Client' },
      { apiKey: 'TRADE_FINANCE_SCF_DPWFS', viewValue: 'SCF by DPWFS' },
      { apiKey: 'TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT', viewValue: 'Trade Finance: Advance Payment Solution – Disbursement to Client' },
      { apiKey: 'LOGISTICS_FINANCE_INVOICE_DISCOUNTING', viewValue: 'Logistics Finance: Invoice Discounting' },
      { apiKey: 'LOGISTICS_FINANCE_INVOICE_FACTORING', viewValue: 'Logistics Finance: Invoice Factoring' },
      { apiKey: 'LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE', viewValue: 'Logistics Finance: Invoice Factoring w/o Recourse' },
      { apiKey: 'LOGISTICS_FINANCE_REVERSE_FACTORING', viewValue: 'Logistics Finance: Reverse Factoring' },
      { apiKey: 'LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP', viewValue: 'Logistics Finance: Advance Payment Solution – Disbursement to Counter Party' },
      { apiKey: 'LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT', viewValue: 'Logistics Finance: Advance Payment Solution – Disbursement to Client' },
      { apiKey: 'LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING', viewValue: 'Logistics Finance: Trade Payables Financing' },
      { apiKey: 'SUPPLY_CHAIN_FINANCING', viewValue: 'Supply Chain Financing' },
    ],
    restrictDisbursementFormControl: [
      {
        control: 'restrictDisbursalInvoiceDiscounting',
        value: false,
        label: 'Restrict invoice discounting disbursement for approved buyer list in facility',
        apiKey: 'restrict_disbursal_ID'
      },
      {
        control: 'restrictDisbursalReverseFactoring',
        value: false,
        label: 'Restrict reverse factoring disbursement for approved seller list in facility',
        apiKey: 'restrict_disbursal_RF'
      },
      {
        control: 'restrictDisbursalTradePayables',
        value: false,
        label: 'Restrict trade payables financing disbursement for approved seller list in facility',
        apiKey: 'restrict_disbursal_TP'
      },
      {
        control: 'restrictDisbursalInventoryFinanceBuyer',
        value: false,
        label: 'Restrict inventory finance buyer disbursement for approved buyer list in facility',
        apiKey: 'restrict_disbursal_IVFB'
      },
      {
        control: 'restrictDisbursalInventoryFinanceSeller',
        value: false,
        label: 'Restrict inventory finance seller disbursement for approved buyer list in facility',
        apiKey: 'restrict_disbursal_IVFS'
      },
      {
        control: 'restrictDisbursalRevolvingCreditFacility',
        value: false,
        label: 'Restrict revolving credit facility disbursement for approved buyer list in facility',
        apiKey: 'restrict_disbursal_RCF'
      },
      {
        control: 'restrictDisbursalReverseFactoringSeller',
        value: false,
        label: 'Restrict reverse factoring seller disbursement for approved seller list in facility',
        apiKey: 'restrict_disbursal_RFS'
      },
      {
        control: 'restrictDisbursalMRPA',
        value: false,
        label: 'Restrict MRPA disbursement for approved seller list in facility',
        apiKey: 'restrict_disbursal_MRPA'
      },
      {
        control: 'restrictDisbursalMRPAC',
        value: false,
        label: 'Restrict MRPA Client disbursement for approved seller list in facility',
        apiKey: 'restrict_disbursal_MRPAC'
      },
      {
        control: 'restrictDisbursalSCFDPWFS',
        value: false,
        label: 'Restrict SCF DPWFS disbursement for approved seller list in facility',
        apiKey: 'restrict_disbursal_SCFDPWFS'
      },
      {
        control: 'restrictDisbursalAPSC',
        value: false,
        label: 'Restrict APSC disbursement for approved buyer list in facility',
        apiKey: 'restrict_disbursal_APSC'
      },
      {
        control: 'restrictDisbursalAPSCP',
        value: false,
        label: 'Restrict APSCP disbursement for approved buyer list in facility',
        apiKey: 'restrict_disbursal_APSCP'
      }
    ],
    incorporationCountryControls: [
      {
        productType: 'INVOICE_DISCOUNTING',
        control: 'clientIncCountryID',
        apiKey: 'client_incorporation_countries_ID',
        value: false,
        label: 'Applicant Incorporation Countries - Invoice Discounting',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Seller/Exporter Country',
        enableAllSelection: ''
      },
      {
        productType: 'INVOICE_FACTORING',
        control: 'clientIncCountryIF',
        apiKey: 'client_incorporation_countries_IF',
        value: false,
        label: 'Applicant Incorporation Countries - Invoice Factoring',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Seller/Exporter Country',
        enableAllSelection: ''
      },
      {
        productType: 'INVOICE_FACTORING',
        control: 'importerIncCountryIF',
        apiKey: 'counter_party_incorporation_countries_IF',
        value: false,
        label: 'Importer (Counter Party) Incorporation Countries - Invoice Factoring',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Buyer/Importer Country',
        enableAllSelection: 'Countries'
      },
      {
        productType: 'INVOICE_FACTORING_WO_RECOURSE',
        control: 'clientIncCountryIFWOR',
        apiKey: 'client_incorporation_countries_IFWOR',
        value: false,
        label: 'Applicant Incorporation Countries - Invoice Factoring w/o Recourse',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Seller/Exporter Country',
        enableAllSelection: ''
      },
      {
        productType: 'INVOICE_FACTORING_WO_RECOURSE',
        control: 'importerIncCountryIFWOR',
        apiKey: 'counter_party_incorporation_countries_IFWOR',
        value: false,
        label: 'Importer (Counter Party) Incorporation Countries - Invoice Factoring w/o Recourse',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Buyer/Importer Country',
        enableAllSelection: 'Countries'
      },
      {
        productType: 'REVERSE_FACTORING',
        control: 'clientIncCountryRF',
        apiKey: 'client_incorporation_countries_RF',
        value: false,
        label: 'Applicant Incorporation Countries - Reverse Factoring',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Buyer/Importer Country',
        enableAllSelection: ''
      },
      {
        productType: 'TRADE_PAYABLES_FINANCING',
        control: 'clientIncCountryTPF',
        apiKey: 'client_incorporation_countries_TP',
        value: false,
        label: 'Applicant Incorporation Countries - Trade Payables Financing',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Buyer/Importer Country',
        enableAllSelection: ''
      },
      {
        productType: 'ADVANCE_PAYMENT_SOLUTION_CP',
        control: 'clientIncCountryAPSCP',
        apiKey: 'client_incorporation_countries_APSCP',
        value: false,
        label: 'Applicant Incorporation Countries - Advance Payment Solution – Disbursement to Counter Party',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Buyer/Importer Country',
        enableAllSelection: ''
      },
      {
        productType: 'INVENTORY_FINANCE_BUYER',
        control: 'clientIncCountryIVFB',
        apiKey: 'client_incorporation_countries_IVFB',
        value: false,
        label: 'Applicant Incorporation Countries - Inventory Finance Buyer',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Seller/Exporter Country',
        enableAllSelection: ''
      },
      {
        productType: 'INVENTORY_FINANCE_SELLER',
        control: 'clientIncCountryIVFS',
        apiKey: 'client_incorporation_countries_IVFS',
        value: false,
        label: 'Applicant Incorporation Countries - Inventory Finance Seller',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Seller/Exporter Country',
        enableAllSelection: ''
      },
      {
        productType: 'REVOLVING_CREDIT_FACILITY',
        control: 'clientIncCountryRCF',
        apiKey: 'client_incorporation_countries_RCF',
        value: false,
        label: 'Applicant Incorporation Revolving Credit Facility',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Seller/Exporter Country',
        enableAllSelection: ''
      },
      {
        productType: 'SUPPLY_CHAIN_FINANCING',
        control: 'clientIncCountrySCF',
        apiKey: 'client_incorporation_countries_SCF',
        value: false,
        label: 'Applicant Incorporation Countries - Supply Chain Financing',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Buyer/Importer Country',
        enableAllSelection: ''
      },
      {
        productType: 'REVERSE_FACTORING_SELLER',
        control: 'clientIncCountryRFS',
        apiKey: 'client_incorporation_countries_RFS',
        value: false,
        label: 'Applicant Incorporation Countries - Reverse Factoring - Charges Borne by Seller',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: '',
        enableAllSelection: ''
      },
      {
        productType: 'MRPA',
        control: 'clientIncCountryMRPA',
        apiKey: 'client_incorporation_countries_MRPA',
        value: false,
        label: 'Applicant Incorporation Countries - MRPA',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: '',
        enableAllSelection: ''
      },
      {
        productType: 'MRPA_CLIENT',
        control: 'clientIncCountryMRPAC',
        apiKey: 'client_incorporation_countries_MRPAC',
        value: false,
        label: 'Applicant Incorporation Countries - MRPA - Disbursement to Client',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: '',
        enableAllSelection: 'Countries'
      },
      {
        productType: 'SCF_DPWFS',
        control: 'clientIncCountrySCFDPWFS',
        apiKey: 'client_incorporation_countries_SCFDPWFS',
        value: false,
        label: 'Applicant Incorporation Countries - SCF by DPWFS',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: '',
        enableAllSelection: ''
      },
      {
        productType: 'ADVANCE_PAYMENT_SOLUTION_CLIENT',
        control: 'clientIncCountryAPSC',
        apiKey: 'client_incorporation_countries_APSC',
        value: false,
        label: 'Applicant Incorporation Countries - Advance Payment Solution – Disbursement to Client',
        selectedCountries: [],
        sourceCountries: [],
        fieldVisibility: false,
        tooltip: 'Seller/Exporter Country',
        enableAllSelection: ''
      },
    ]
  }

  productCategories = ['trade', 'logistics'];

  defaultDisplaySelectedFinance: string = 'Logistics Finance: Invoice Discounting';
  defaultSelectedFinance: FinanceTypeInfoInterface = {
    productCategory: {
      value: '2',
      viewValue: 'Logistics Finance'
    },
    productType: {
      value: '1',
      viewValue: 'Invoice Discounting'
    }
  };

  conditionMapConfig = {
    productCategory: {
      TRADE_FINANCE: {
        label: 'TRADE FINANCE',
        firstName: 'TRADE',
        definition: 'Pre & Post-shipment financing for your imports & exports',
        buyerImporterLabel: 'Importer',
        sellerExporterLabel: 'Exporter'
      },
      LOGISTICS_FINANCE: {
        label: 'LOGISTICS FINANCE',
        firstName: 'LOGISTICS',
        definition: 'Receivables and Payables financing for sales and purchase of logistics services',
        buyerImporterLabel: 'Buyer',
        sellerExporterLabel: 'Seller'
      }
    },
    productType: {
      INVOICE_DISCOUNTING: 'INVOICE DISCOUNTING',
      INVOICE_FACTORING: 'INVOICE FACTORING',
      REVERSE_FACTORING: 'REVERSE FACTORING',
      TRADE_PAYABLES_FINANCING: 'TRADE PAYABLES FINANCING',
      ADVANCE_PAYMENT_SOLUTION_CP: 'ADVANCE PAYMENT SOLUTION DISBURSEMENT TO COUNTER_PARTY',
      INVOICE_FACTORING_WO_RECOURSE: 'INVOICE FACTORING w/o RECOURSE',
      INVENTORY_FINANCE_BUYER: 'INVENTORY FINANCE BUYER',
      INVENTORY_FINANCE_SELLER: 'INVENTORY FINANCE SELLER',
      REVOLVING_CREDIT_FACILITY: 'REVOLVING CREDIT FACILITY',
      REVERSE_FACTORING_SELLER: 'REVERSE FACTORING - CHARGES BORNE BY SELLER',
      MRPA: 'MRPA',
      MRPA_CLIENT: 'MRPA DISBURSEMENT TO CLIENT',
      SCF_DPWFS: 'SCF BY DPWFS',
      APSC: 'ADVANCE PAYMENT SOLUTION DISBURSEMENT TO CLIENT',
    },
    financingType: {
      DEFERRED: 'DEFERRED',
      IN_TRANSIT: 'IN-TRANSIT'
    }
  };

  userRoleFinTypeDesciption = {
    logistics_finance_invoice_discounting: ['LF_INVDISC_AMEND', 'LF_INVDISC_VW'],
    logistics_finance_invoice_factoring: ['LF_INVFAC_AMEND', 'LF_INVFAC_VW'],
    logistics_finance_reverse_factoring: ['LF_REVFAC_AMEND', 'LF_REVFAC_VW'],
    logistics_finance_trade_payables_financing: ['LF_TRDPAYFIN_AMEND', 'LF_TRDPAYFIN_VW'],
    logistics_finance_advance_payment_solution_cp: ['LF_APSCP_VW', 'LF_APSCP_AMEND'],
    logistics_finance_advance_payment_solution_client: ['LF_APSC_VW', 'LF_APSC_AMEND'],
    logistics_finance_invoice_factoring_wo_recourse: ['LF_INVFACWOREC_AMEND', 'LF_INVFACWOREC_VW'],
    trade_finance_invoice_discounting: ['TF_INVDISC_AMEND', 'TF_INVDISC_VW'],
    trade_finance_invoice_factoring: ['TF_INVFAC_AMEND', 'TF_INVFAC_VW'],
    trade_finance_reverse_factoring: ['TF_REVFAC_AMEND', 'TF_REVFAC_VW'],
    trade_finance_trade_payables_financing: ['TF_TRDPAYFIN_AMEND', 'TF_TRDPAYFIN_VW'],
    trade_finance_advance_payment_solution_cp: ['TF_APSCP_VW', 'TF_APSCP_AMEND'],
    trade_finance_invoice_factoring_wo_recourse: ['TF_INVFACWOREC_AMEND', 'TF_INVFACWOREC_VW'],
    supply_chain_financing: ['SCF_AMEND', 'SCF_VIEW'],
    trade_finance_inventory_finance_buyer: ['INV_FIN_AMEND', 'INV_FIN_VW'],
    trade_finance_inventory_finance_seller: ['INV_FIN_AMEND', 'INV_FIN_VW'],
    trade_finance_revolving_credit_facility: ['RCF_AMEND', 'RCF_VW'],
    trade_finance_reverse_factoring_seller: ['RF_SELLER_AMEND', 'RF_SELLER_VW'],
    trade_finance_mrpa: ['MRPA_AMEND', 'MRPA_VW'],
    trade_finance_mrpa_client: ['MRPA_CLIENT_AMEND', 'MRPA_CLIENT_VW'],
    trade_finance_scf_dpwfs: ['SCF_DPWFS_AMEND', 'SCF_DPWFS_VW'],
    trade_finance_advance_payment_solution_client: ['TF_APSC_VW', 'TF_APSC_AMEND'],
  };

  allFinances = [
    { value: '2', viewValue: 'Trade Finance: Invoice Factoring' },
    { value: '1', viewValue: 'Trade Finance: Invoice Discounting' },
    { value: '3', viewValue: 'Trade Finance: Reverse Factoring' },
    { value: '4', viewValue: 'Trade Finance: Trade Payables Financing' },
    { value: '5', viewValue: 'Trade Finance: Invoice Factoring w/o Recourse' },
    { value: '6', viewValue: 'Inventory Finance Buyer' },
    { value: '7', viewValue: 'Inventory Finance Seller' },
    { value: '8', viewValue: 'Revolving Credit Facility' },
    { value: '2', viewValue: 'Logistics Finance: Invoice Factoring' },
    { value: '1', viewValue: 'Logistics Finance: Invoice Discounting' },
    { value: '3', viewValue: 'Logistics Finance: Reverse Factoring' },
    { value: '4', viewValue: 'Logistics Finance: Trade Payables Financing' },
    { value: '5', viewValue: 'Logistics Finance: Invoice Factoring w/o Recourse' },
    { value: '9', viewValue: 'Reverse Factoring - Charges Borne by Seller' },
    { value: '10', viewValue: 'MRPA' },
    { value: '11', viewValue: 'MRPA - Disbursement to Client' },
    { value: '12', viewValue: 'SCF by DPWFS' },
    { value: '13', viewValue: 'Trade Finance: Advance Payment Solution – Disbursement to Client' },
    { value: '14', viewValue: 'Trade Finance: Advance Payment Solution – Disbursement to Counter Party' },
    { value: '13', viewValue: 'Logistics Finance: Advance Payment Solution – Disbursement to Client' },
    { value: '14', viewValue: 'Logistics Finance: Advance Payment Solution – Disbursement to Counter Party' },
  ];


  productTypeCodesForApplyCFTable = {
    LF_ID: `Logistics Finance: Invoice Discounting`,
    LF_IF: `Logistics Finance: Invoice Factoring`,
    LF_RF: `Logistics Finance: Reverse Factoring`,
    LF_TP: `Logistics Finance: Trade Payables Financing`,
    LF_APSCP: `Logistics Finance: Advance Payment Solution – Disbursement to Counter Party`,
    LF_APSC: `Logistics Finance: Advance Payment Solution – Disbursement to Client`,
    LF_IFWOR: `Logistics Finance: Invoice Factoring w/o Recourse`,
    TF_IF: `Trade Finance: Invoice Factoring`,
    TF_ID: `Trade Finance: Invoice Discounting`,
    TF_RF: `Trade Finance: Reverse Factoring`,
    TF_TP: `Trade Finance: Trade Payables Financing`,
    TF_APSCP: `Trade Finance: Advance Payment Solution – Disbursement to Counter Party`,
    TF_IVF: `Inventory Finance`,
    TF_IVFB: `Inventory Finance Buyer`,
    TF_IVFS: `Inventory Finance Seller`,
    TF_IFWOR: `Trade Finance: Invoice Factoring w/o Recourse`,
    TF_RCF: `Revolving Credit Facility`,
    TF_RFS: `Reverse Factoring - Charges Borne by Seller`,
    TF_MRPA: `MRPA`,
    TF_MRPAC: `MRPA - Disbursement to Client`,
    TF_SCFDPWFS: `SCF by DPWFS`,
    TF_APSC: `Trade Finance: Advance Payment Solution – Disbursement to Client`
  };

  counterPartyTitleMapping = {
    // without category and financing type
    INVOICE_FACTORING: 'Invoice Factoring',
    INVOICE_DISCOUNTING: 'Invoice Discounting',
    REVERSE_FACTORING: 'Reverse Factoring',
    TRADE_PAYABLES_FINANCING: 'Trade Payables Financing',
    ADVANCE_PAYMENT_SOLUTION_CP: 'Advance Payment Solution – Disbursement to Counter Party',
    INVOICE_FACTORING_WO_RECOURSE: 'Invoice Factoring w/o Recourse',
    REVERSE_FACTORING_SELLER: 'Reverse Factoring - Charges Borne by Seller',
    MRPA: 'MRPA',
    MRPA_CLIENT: 'MRPA - Disbursement to Client',
    SCF_DPWFS: 'SCF by DPWFS',
    INVENTORY_FINANCE: 'Inventory Finance',
    REVOLVING_CREDIT_FACILITY: 'Revolving Credit Facility',
    ADVANCE_PAYMENT_SOLUTION_CLIENT: 'Advance Payment Solution – Disbursement to Client',

    //with category and finance type as in transit
    LOGISTICS_FINANCE_INVOICE_FACTORING_IN_TRANSIT: 'Invoice Factoring - Logistics Finance - IN TRANSIT',
    LOGISTICS_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT: 'Invoice Discounting - Logistics Finance - IN TRANSIT',
    LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT: 'Invoice Factoring w/o Recourse - Logistics Finance - IN TRANSIT',
    TRADE_FINANCE_INVOICE_FACTORING_IN_TRANSIT: 'Invoice Factoring - Trade Finance - IN TRANSIT',
    TRADE_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT: 'Invoice Discounting - Trade Finance - IN TRANSIT',
    TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT: 'Invoice Factoring w/o Recourse - Trade Finance - IN TRANSIT',
    LOGISTICS_FINANCE_REVERSE_FACTORING_IN_TRANSIT: 'Reverse Factoring - Logistics Finance - IN TRANSIT',
    LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT: 'Trade Payables Financing - Logistics Finance - IN TRANSIT',
    LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT: 'Advance Payment Solution – Disbursement to Client - Logistics Finance - IN TRANSIT',
    LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT: 'Advance Payment Solution – Disbursement to Counter Party - Logistics Finance - IN TRANSIT',
    TRADE_FINANCE_REVERSE_FACTORING_IN_TRANSIT: 'Reverse Factoring - Trade Finance - IN TRANSIT',
    TRADE_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT: 'Trade Payables Financing - Trade Finance - IN TRANSIT',
    TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT: 'Advance Payment Solution – Disbursement to Client - Trade Finance - IN TRANSIT',
    TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT: 'Advance Payment Solution – Disbursement to Counter Party - Trade Finance - IN TRANSIT',
    TRADE_FINANCE_REVERSE_FACTORING_SELLER_IN_TRANSIT: 'Reverse Factoring - Charges Borne by Seller - IN TRANSIT',
    TRADE_FINANCE_MRPA_IN_TRANSIT: 'MRPA - IN TRANSIT',
    TRADE_FINANCE_MRPA_CLIENT_IN_TRANSIT: 'MRPA - Disbursement to Client - IN TRANSIT',
    TRADE_FINANCE_SCF_DPWFS_IN_TRANSIT: 'SCF by DPWFS - IN TRANSIT',

    //with category and finance type as deferred
    LOGISTICS_FINANCE_REVERSE_FACTORING_DEFERRED: 'Reverse Factoring - Logistics Finance - DEFERRED',
    LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED: 'Trade Payables Financing - Logistics Finance - DEFERRED',
    LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED: 'Advance Payment Solution – Disbursement to Client - Logistics Finance - DEFERRED',
    LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED: 'Advance Payment Solution – Disbursement to Counter Party - Logistics Finance - DEFERRED',
    TRADE_FINANCE_REVERSE_FACTORING_DEFERRED: 'Reverse Factoring - Trade Finance - DEFERRED',
    TRADE_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED: 'Trade Payables Financing - Trade Finance - DEFERRED',
    TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED: 'Advance Payment Solution – Disbursement to Counter Party - Trade Finance - DEFERRED',
    TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED: 'Advance Payment Solution – Disbursement to Client - Trade Finance - DEFERRED',
    LOGISTICS_FINANCE_INVOICE_FACTORING_DEFERRED: 'Invoice Factoring - Logistics Finance - DEFERRED',
    LOGISTICS_FINANCE_INVOICE_DISCOUNTING_DEFERRED: 'Invoice Discounting - Logistics Finance - DEFERRED',
    LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED: 'Invoice Factoring w/o Recourse - Logistics Finance - DEFERRED',
    TRADE_FINANCE_INVOICE_FACTORING_DEFERRED: 'Invoice Factoring - Trade Finance - DEFERRED',
    TRADE_FINANCE_INVOICE_DISCOUNTING_DEFERRED: 'Invoice Discounting - Trade Finance - DEFERRED',
    TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED: 'Invoice Factoring w/o Recourse - Trade Finance - DEFERRED',
    TRADE_FINANCE_REVERSE_FACTORING_SELLER_DEFERRED: 'Reverse Factoring - Charges Borne by Seller - DEFERRED',
    TRADE_FINANCE_MRPA_DEFERRED: 'MRPA - DEFERRED',
    TRADE_FINANCE_MRPA_CLIENT_DEFERRED: 'MRPA - Disbursement to Client - DEFERRED',
    TRADE_FINANCE_SCF_DPWFS_DEFERRED: 'SCF by DPWFS - DEFERRED',
    TRADE_FINANCE_INVENTORY_FINANCE_SELLER_DEFERRED: 'Inventory Finance Seller - DEFERRED',
    TRADE_FINANCE_INVENTORY_FINANCE_BUYER_DEFERRED: 'Inventory Finance Buyer - DEFERRED',
    TRADE_FINANCE_REVOLVING_CREDIT_FACILITY_DEFERRED: 'Revolving Credit Facility - DEFERRED',
  }

  counterPartyTitleToKeyMapping = {
    "Invoice Factoring": "INVOICE_FACTORING",
    "Invoice Discounting": "INVOICE_DISCOUNTING",
    "Reverse Factoring": "REVERSE_FACTORING",
    "Trade Payables Financing": "TRADE_PAYABLES_FINANCING",
    "Advance Payment Solution – Disbursement to Counter Party": "ADVANCE_PAYMENT_SOLUTION_CP",
    "Invoice Factoring w/o Recourse": "INVOICE_FACTORING_WO_RECOURSE",
    "Reverse Factoring - Charges Borne by Seller": "REVERSE_FACTORING_SELLER",
    "MRPA": "MRPA",
    "MRPA - Disbursement to Client": "MRPA_CLIENT",
    "SCF by DPWFS": "SCF_DPWFS",
    "Inventory Finance": "INVENTORY_FINANCE",
    "Revolving Credit Facility": "REVOLVING_CREDIT_FACILITY",
    "Advance Payment Solution – Disbursement to Client": "ADVANCE_PAYMENT_SOLUTION_CLIENT",
    "Invoice Factoring - Logistics Finance - IN TRANSIT": "LOGISTICS_FINANCE_INVOICE_FACTORING_IN_TRANSIT",
    "Invoice Discounting - Logistics Finance - IN TRANSIT": "LOGISTICS_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT",
    "Invoice Factoring w/o Recourse - Logistics Finance - IN TRANSIT": "LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT",
    "Invoice Factoring - Trade Finance - IN TRANSIT": "TRADE_FINANCE_INVOICE_FACTORING_IN_TRANSIT",
    "Invoice Discounting - Trade Finance - IN TRANSIT": "TRADE_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT",
    "Invoice Factoring w/o Recourse - Trade Finance - IN TRANSIT": "TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT",
    "Reverse Factoring - Logistics Finance - IN TRANSIT": "LOGISTICS_FINANCE_REVERSE_FACTORING_IN_TRANSIT",
    "Trade Payables Financing - Logistics Finance - IN TRANSIT": "LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT",
    "Advance Payment Solution – Disbursement to Client - Logistics Finance - IN TRANSIT": "LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT",
    "Advance Payment Solution – Disbursement to Counter Party - Logistics Finance - IN TRANSIT": "LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT",
    "Reverse Factoring - Trade Finance - IN TRANSIT": "TRADE_FINANCE_REVERSE_FACTORING_IN_TRANSIT",
    "Trade Payables Financing - Trade Finance - IN TRANSIT": "TRADE_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT",
    "Advance Payment Solution – Disbursement to Client - Trade Finance - IN TRANSIT": "TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT",
    "Advance Payment Solution – Disbursement to Counter Party - Trade Finance - IN TRANSIT": "TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT",
    "Reverse Factoring - Charges Borne by Seller - IN TRANSIT": "TRADE_FINANCE_REVERSE_FACTORING_SELLER_IN_TRANSIT",
    "MRPA - IN TRANSIT": "TRADE_FINANCE_MRPA_IN_TRANSIT",
    "MRPA - Disbursement to Client - IN TRANSIT": "TRADE_FINANCE_MRPA_CLIENT_IN_TRANSIT",
    "SCF by DPWFS - IN TRANSIT": "TRADE_FINANCE_SCF_DPWFS_IN_TRANSIT",
    "Reverse Factoring - Logistics Finance - DEFERRED": "LOGISTICS_FINANCE_REVERSE_FACTORING_DEFERRED",
    "Trade Payables Financing - Logistics Finance - DEFERRED": "LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED",
    "Advance Payment Solution – Disbursement to Client - Logistics Finance - DEFERRED": "LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED",
    "Advance Payment Solution – Disbursement to Counter Party - Logistics Finance - DEFERRED": "LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED",
    "Reverse Factoring - Trade Finance - DEFERRED": "TRADE_FINANCE_REVERSE_FACTORING_DEFERRED",
    "Trade Payables Financing - Trade Finance - DEFERRED": "TRADE_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED",
    "Advance Payment Solution – Disbursement to Counter Party - Trade Finance - DEFERRED": "TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED",
    "Advance Payment Solution – Disbursement to Client - Trade Finance - DEFERRED": "TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED",
    "Invoice Factoring - Logistics Finance - DEFERRED": "LOGISTICS_FINANCE_INVOICE_FACTORING_DEFERRED",
    "Invoice Discounting - Logistics Finance - DEFERRED": "LOGISTICS_FINANCE_INVOICE_DISCOUNTING_DEFERRED",
    "Invoice Factoring w/o Recourse - Logistics Finance - DEFERRED": "LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED",
    "Invoice Factoring - Trade Finance - DEFERRED": "TRADE_FINANCE_INVOICE_FACTORING_DEFERRED",
    "Invoice Discounting - Trade Finance - DEFERRED": "TRADE_FINANCE_INVOICE_DISCOUNTING_DEFERRED",
    "Invoice Factoring w/o Recourse - Trade Finance - DEFERRED": "TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED",
    "Reverse Factoring - Charges Borne by Seller - DEFERRED": "TRADE_FINANCE_REVERSE_FACTORING_SELLER_DEFERRED",
    "MRPA - DEFERRED": "TRADE_FINANCE_MRPA_DEFERRED",
    "MRPA - Disbursement to Client - DEFERRED": "TRADE_FINANCE_MRPA_CLIENT_DEFERRED",
    "SCF by DPWFS - DEFERRED": "TRADE_FINANCE_SCF_DPWFS_DEFERRED",
    "Inventory Finance Seller - DEFERRED": "TRADE_FINANCE_INVENTORY_FINANCE_SELLER_DEFERRED",
    "Inventory Finance Buyer - DEFERRED": "TRADE_FINANCE_INVENTORY_FINANCE_BUYER_DEFERRED",
    "Revolving Credit Facility - DEFERRED": "TRADE_FINANCE_REVOLVING_CREDIT_FACILITY_DEFERRED",
  }

  FINANCE_TYPE_CODE_MAPPING = {
    LOGISTIC_FINANCE_PAYABLE_REVERSE_FACTORING: 'Logistics Finance - Payables (Reverse Factoring)',
    LOGISTICS_FINANCE_TRADE_PAYABLE_FINANCING: 'Logistics Finance - Payables (Trade Payables Financing)',
    LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP: 'Logistics Finance - Payables (Advance Payment Solution – Disbursement to Counter Party)',
    LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT: 'Logistics Finance - Advance Payment Solution – Disbursement to Client',
    LOGISTIC_FINANCE_RECEIVABLE_INVOICE_DISCOUNTING: 'Logistics Finance - Receivables (Invoice Discounting)',
    LOGISTIC_FINANCE_RECEIVABLE_INVOICE_FACTORING: 'Logistics Finance - Receivables (Invoice Factoring)',
    LOGISTIC_FINANCE_REC_INVOICE_FACTORING_WO_REC: 'Logistics Finance - Receivables (Invoice Factoring w/o Recourse)',
    TRADE_FINANCE_PAYABLE_REVERSE_FACTORING: 'Trade Finance - Payables (Reverse Factoring)',
    TRADE_FINANCE_TRADE_PAYABLE_FINANCING: 'Trade Finance - Payables (Trade Payables Financing)',
    TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP: 'Trade Finance - Payables (Advance Payment Solution – Disbursement to Counter Party)',
    TRADE_FINANCE_RECEIVABLE_INVOICE_DISCOUNTING: 'Trade Finance - Receivables (Invoice Discounting)',
    TRADE_FINANCE_RECEIVABLE_INVOICE_FACTORING: 'Trade Finance - Receivables (Invoice Factoring)',
    TRADE_FINANCE_REC_INVOICE_FACTORING_WO_REC: 'Trade Finance - Receivables (Invoice Factoring w/o Recourse)',
    TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT: 'Trade Finance - Advance Payment Solution – Disbursement to Client',
    INVENTORY_FINANCE: 'Inventory Finance',
    REVOLVING_CREDIT_FACILITY: 'Revolving Credit Facility',
    BUY_SELL: 'Buy Sell',
    REVERSE_FACTORING_SELLER: 'Reverse Factoring - Charges Borne by Seller',
    MRPA: 'MRPA',
    MRPA_CLIENT: 'MRPA - Disbursement to Client',
    SCF_DPWFS: 'SCF by DPWFS',
  };

  DETAILED_FINANCE_TYPE_CODE_MAPPING = {
    LOGISTIC_FINANCE_PAYABLE_REVERSE_FACTORING: 'Logistics Finance (for Freight Forwarders) - Payables (Reverse Factoring)',
    LOGISTICS_FINANCE_TRADE_PAYABLE_FINANCING: 'Logistics Finance (for Freight Forwarders) - Payables (Trade Payables Financing)',
    LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP: 'Logistics Finance (for Freight Forwarders) - Payables (Advance Payment Solution – Disbursement to Counter Party)',
    LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT: 'Logistics Finance (for Freight Forwarders) - Advance Payment Solution – Disbursement to Client',
    LOGISTIC_FINANCE_RECEIVABLE_INVOICE_DISCOUNTING: 'Logistics Finance (for Freight Forwarders) - Receivables (Invoice Discounting)',
    LOGISTIC_FINANCE_RECEIVABLE_INVOICE_FACTORING: 'Logistics Finance (for Freight Forwarders) - Receivables (Invoice Factoring)',
    LOGISTIC_FINANCE_REC_INVOICE_FACTORING_WO_REC: 'Logistics Finance (for Freight Forwarders) - Receivables (Invoice Factoring w/o Recourse)',
    TRADE_FINANCE_PAYABLE_REVERSE_FACTORING: 'Trade Finance (for BCOs; Importers & Exporters) - Payables (Reverse Factoring)',
    TRADE_FINANCE_TRADE_PAYABLE_FINANCING: 'Trade Finance (for BCOs; Importers & Exporters) - Payables (Trade Payables Financing)',
    TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP: 'Trade Finance (for BCOs; Importers & Exporters) - Payables (Advance Payment Solution – Disbursement to Counter Party)',
    TRADE_FINANCE_RECEIVABLE_INVOICE_DISCOUNTING: 'Trade Finance (for BCOs; Importers & Exporters) - Receivables (Invoice Discounting)',
    TRADE_FINANCE_RECEIVABLE_INVOICE_FACTORING: 'Trade Finance (for BCOs; Importers & Exporters) - Receivables (Invoice Factoring)',
    TRADE_FINANCE_REC_INVOICE_FACTORING_WO_REC: 'Trade Finance (for BCOs; Importers & Exporters) - Receivables (Invoice Factoring w/o Recourse)',
    TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT: 'Trade Finance (for BCOs; Importers & Exporters) - Advance Payment Solution – Disbursement to Client',
    INVENTORY_FINANCE: 'Inventory Finance',
    REVOLVING_CREDIT_FACILITY: 'Revolving Credit Facility',
    BUY_SELL: 'Buy Sell',
    REVERSE_FACTORING_SELLER: 'Reverse Factoring - Charges Borne by Seller',
    MRPA: 'MRPA',
    MRPA_CLIENT: 'MRPA - Disbursement to Client',
    SCF_DPWFS: 'SCF by DPWFS',
  };

  productsToKYCProductsMAP = {
    'Trade Finance: Invoice Factoring': "TRADE_FINANCE_RECEIVABLE_INVOICE_FACTORING",
    'Trade Finance: Invoice Factoring w/o Recourse': 'TRADE_FINANCE_REC_INVOICE_FACTORING_WO_REC',
    'Trade Finance: Invoice Discounting': "TRADE_FINANCE_RECEIVABLE_INVOICE_DISCOUNTING",
    'Trade Finance: Reverse Factoring': "TRADE_FINANCE_PAYABLE_REVERSE_FACTORING",
    'Trade Finance: Trade Payables Financing': "TRADE_FINANCE_TRADE_PAYABLE_FINANCING",
    'Trade Finance: Advance Payment Solution – Disbursement to Counter Party': "TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP",
    'Trade Finance: Advance Payment Solution – Disbursement to Client': 'TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT',
    'Logistics Finance: Invoice Factoring': "LOGISTIC_FINANCE_RECEIVABLE_INVOICE_FACTORING",
    'Logistics Finance: Invoice Factoring w/o Recourse': 'LOGISTIC_FINANCE_REC_INVOICE_FACTORING_WO_REC',
    'Logistics Finance: Invoice Discounting': "LOGISTIC_FINANCE_RECEIVABLE_INVOICE_DISCOUNTING",
    'Logistics Finance: Reverse Factoring': "LOGISTIC_FINANCE_PAYABLE_REVERSE_FACTORING",
    'Logistics Finance: Trade Payables Financing': "LOGISTIC_FINANCE_TRADE_PAYABLE_FINANCING",
    'Logistics Finance: Advance Payment Solution – Disbursement to Counter Party': "LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP",
    'Logistics Finance: Advance Payment Solution – Disbursement to Client': "LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT",
    'Inventory Finance Buyer': 'INVENTORY_FINANCE',
    'Inventory Finance Seller': 'INVENTORY_FINANCE',
    'Revolving Credit Facility': 'REVOLVING_CREDIT_FACILITY',
    'Reverse Factoring - Charges Borne by Seller': 'REVERSE_FACTORING_SELLER',
    'MRPA': 'MRPA',
    'MRPA - Disbursement to Client': 'MRPA_CLIENT',
    'SCF by DPWFS': 'SCF_DPWFS',
  }

  getProductOptions(): any[] {
    const uniqueProducts = Object.keys(this.productsToKYCProductsMAP);
    return uniqueProducts.map((item, index) => ({ value: index, name: item }));
  }

  allProductDefinitions = [
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'TRADE_FINANCE',
      productType: 'INVOICE_DISCOUNTING',
      description: 'Invoice Discounting - Trade Finance',
      mawaniRefKey: 'trade_finance_invoice_discounting'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'TRADE_FINANCE',
      productType: 'INVOICE_FACTORING',
      description: 'Invoice Factoring - Trade Finance',
      mawaniRefKey: 'trade_finance_invoice_factoring'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'TRADE_FINANCE',
      productType: 'INVOICE_FACTORING_WO_RECOURSE',
      description: 'Invoice Factoring w/o Recourse - Trade Finance',
      mawaniRefKey: 'trade_finance_invoice_factoring_wo_recourse'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'TRADE_FINANCE',
      productType: 'REVERSE_FACTORING',
      description: 'Reverse Factoring - Trade Finance',
      mawaniRefKey: 'trade_finance_reverse_factoring'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'TRADE_FINANCE',
      productType: 'TRADE_PAYABLES_FINANCING',
      description: 'Trade Payables Financing - Trade Finance',
      mawaniRefKey: 'trade_finance_trade_payables_financing'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'TRADE_FINANCE',
      productType: 'ADVANCE_PAYMENT_SOLUTION_CP',
      description: 'Advance Payment Solution – Disbursement to Counter Party - Trade Finance',
      mawaniRefKey: 'trade_finance_advance_payment_solution_cp'
    },
    {
      superCategory: 'LOAN_OR_ADVANCE',
      category: 'TRADE_FINANCE',
      productType: 'INVENTORY_FINANCE_BUYER',
      description: 'Inventory Finance Buyer',
      mawaniRefKey: 'trade_finance_inventory_finance_buyer'
    },
    {
      superCategory: 'LOAN_OR_ADVANCE',
      category: 'TRADE_FINANCE',
      productType: 'INVENTORY_FINANCE_SELLER',
      description: 'Inventory Finance Seller',
      mawaniRefKey: 'trade_finance_inventory_finance_seller'
    },
    {
      superCategory: 'LOAN_OR_ADVANCE',
      category: 'TRADE_FINANCE',
      productType: 'REVOLVING_CREDIT_FACILITY',
      description: 'Revolving Credit Facility',
      mawaniRefKey: 'trade_finance_revolving_credit_facility'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'TRADE_FINANCE',
      productType: 'REVERSE_FACTORING_SELLER',
      description: 'Reverse Factoring - Charges Borne by Seller',
      mawaniRefKey: 'trade_finance_reverse_factoring_seller'
    },
    {
      superCategory: 'FI_FINANCING',
      category: 'TRADE_FINANCE',
      productType: 'MRPA',
      description: 'MRPA',
      mawaniRefKey: 'trade_finance_mrpa'
    },
    {
      superCategory: 'FI_FINANCING',
      category: 'TRADE_FINANCE',
      productType: 'MRPA_CLIENT',
      description: 'MRPA - Disbursement to Client',
      mawaniRefKey: 'trade_finance_mrpa_client'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'TRADE_FINANCE',
      productType: 'SCF_DPWFS',
      description: 'SCF by DPWFS',
      mawaniRefKey: 'trade_finance_scf_dpwfs'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'TRADE_FINANCE',
      productType: 'ADVANCE_PAYMENT_SOLUTION_CLIENT',
      description: 'Advance Payment Solution – Disbursement to Client - Trade Finance',
      mawaniRefKey: 'trade_finance_advance_payment_solution_client'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'LOGISTICS_FINANCE',
      productType: 'INVOICE_DISCOUNTING',
      description: 'Invoice Discounting - Logistics Finance',
      mawaniRefKey: 'logistics_finance_invoice_discounting'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'LOGISTICS_FINANCE',
      productType: 'INVOICE_FACTORING',
      description: 'Invoice Factoring - Logistics Finance',
      mawaniRefKey: 'logistics_finance_invoice_factoring'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'LOGISTICS_FINANCE',
      productType: 'INVOICE_FACTORING_WO_RECOURSE',
      description: 'Invoice Factoring w/o Recourse - Logistics Finance',
      mawaniRefKey: 'logistics_finance_invoice_factoring_wo_recourse'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'LOGISTICS_FINANCE',
      productType: 'REVERSE_FACTORING',
      description: 'Reverse Factoring - Logistics Finance',
      mawaniRefKey: 'logistics_finance_reverse_factoring'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'LOGISTICS_FINANCE',
      productType: 'TRADE_PAYABLES_FINANCING',
      description: 'Trade Payables Financing - Logistics Finance',
      mawaniRefKey: 'logistics_finance_trade_payables_financing'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'LOGISTICS_FINANCE',
      productType: 'ADVANCE_PAYMENT_SOLUTION_CLIENT',
      description: 'Advance Payment Solution – Disbursement to Client - Logistics Finance',
      mawaniRefKey: 'logistics_finance_advance_payment_solution_client'
    },
    {
      superCategory: 'RECEIVABLE_PURCHASE',
      category: 'LOGISTICS_FINANCE',
      productType: 'ADVANCE_PAYMENT_SOLUTION_CP',
      description: 'Advance Payment Solution – Disbursement to Counter Party - Logistics Finance',
      mawaniRefKey: 'logistics_finance_advance_payment_solution_cp'
    },
  ];

  productConfig: ProductConfig = {
    TRADE_FINANCE: {
      INVOICE_DISCOUNTING: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'INVOICE_DISCOUNTING'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Invoice Discounting - Trade Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: 'Invoice Discounting is a form of short-term borrowing against your outstanding invoices. The applicant (seller) gets the cash from the financial institution based on the commercial terms agreed and the applicant (seller) pays back to the financial institution on the due date.'
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'IMPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_ID'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            ALLOW_MASTER_INVOICE_SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            MASTER_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICATION_NUMBER: {
              isVisible: true,
              isEditable: true,
            },
            CONVERSION_DATE: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_INTEREST: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_FEES: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_TAX: {
              isVisible: true,
              isEditable: true
            },
            MASTER_INVOICE_NO: {
              isVisible: true,
              isEditable: true,
              label: 'Master Invoice Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN_WITH_ADD_EDIT',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            MASTER_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Amount',

            },

            MASTER_FUNDING_PERCENTAGE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Percentage',
            },

            MASTER_FUNDING_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Limit',
            },

            MASTER_UTILISED_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Available Limit',
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Counter Party.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  label: 'Invoice Amount',
                  fieldType: 'TEXT_NUMBER'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  label: 'Invoice Amount in',
                  fieldType: 'TEXT_NUMBER'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: false,
              isEditable: false,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        },
        UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            LENDING: {
              isVisible: true,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: true,
              isEditable: true,
              label: 'Agreement between Financial Institution and Applicant'
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      INVOICE_FACTORING: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'INVOICE_FACTORING'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Invoice Factoring - Trade Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `Invoice Factoring is a type of short-term accounts receivable financing, where you effectively 'sell' your outstanding invoices to a third-party commercial finance company. The applicant (seller) gets the cash from the financial institution based on the commercial terms agreed and the trading partner (buyer) pays back to the financial institution on the due date.`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: true, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'IMPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_IF'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            ALLOW_MASTER_INVOICE_SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            MASTER_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICATION_NUMBER: {
              isVisible: true,
              isEditable: true,
            },
            CONVERSION_DATE: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_INTEREST: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_FEES: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_TAX: {
              isVisible: true,
              isEditable: true
            },
            MASTER_INVOICE_NO: {
              isVisible: true,
              isEditable: true,
              label: 'Master Invoice Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            MASTER_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Amount',
            },

            MASTER_FUNDING_PERCENTAGE: {
              isVisible: true,
              label: 'Master Invoice Funding Percentage',
              isEditable: true,
              fieldType: 'TEXT_NUMBER'
            },

            MASTER_FUNDING_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Limit',
            },

            MASTER_UTILISED_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Available Limit',
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Counter Party.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: false,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: true
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: false,
              isEditable: false,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            EXTRA_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            SHORT_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Currency: Amount Received From Counter Party'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Amount Received From Counter Party'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        },
        UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            HBL_NO: {
              isVisible: true,
              isEditable: true,
              isMandatory: true,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            LENDING: {
              isVisible: true,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: true,
              isEditable: true,
              label: 'Agreement between Financial Institution and Applicant/Counter Party'
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      REVERSE_FACTORING: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'REVERSE_FACTORING'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Reverse Factoring - Trade Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: 'The finance can be applied by buyer. This gives the buyer more time to pay while our platform ensures the sellers are paid in-time.'
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'IMPORTER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: true, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_RF'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            ALLOW_MASTER_INVOICE_SELECTION: {
              isVisible: false,
              isEditable: false,
            },
            MASTER_INVOICE_RADIO: {
              isVisible: false,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICATION_NUMBER: {
              isVisible: false,
              isEditable: false,
            },
            CONVERSION_DATE: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_INTEREST: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_FEES: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_TAX: {
              isVisible: true,
              isEditable: true
            },
            MASTER_INVOICE_NO: {
              isVisible: false,
              isEditable: false,
              label: 'Master Invoice Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN_WITH_ADD_EDIT',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            MASTER_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Amount',

            },

            MASTER_FUNDING_PERCENTAGE: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Percentage',
            },

            MASTER_FUNDING_LIMIT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Limit',
            },

            MASTER_UTILISED_LIMIT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Available Limit',
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Buyer.',
                  stalePeriodFromDueDate: true
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  label: 'Invoice Amount',
                  fieldType: 'TEXT_NUMBER'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  label: 'Invoice Amount in',
                  fieldType: 'TEXT_NUMBER'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Disbursement Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: false,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            EXTRA_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: true,
          isEditable: true
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            LENDING: {
              isVisible: false,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: false,
              isEditable: false,
              label: ''
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      TRADE_PAYABLES_FINANCING: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'TRADE_PAYABLES_FINANCING'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Trade Payables Financing - Trade Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: 'The finance can be applied by buyer. This gives the buyer more time to pay while our platform ensures the sellers are paid in-time.'
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'IMPORTER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_TP'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            ALLOW_MASTER_INVOICE_SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            MASTER_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICATION_NUMBER: {
              isVisible: true,
              isEditable: true,
            },
            CONVERSION_DATE: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_INTEREST: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_FEES: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_TAX: {
              isVisible: true,
              isEditable: true
            },
            MASTER_INVOICE_NO: {
              isVisible: true,
              isEditable: true,
              label: 'Master Invoice Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN_WITH_ADD_EDIT',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            MASTER_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Amount',

            },

            MASTER_FUNDING_PERCENTAGE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Percentage',
            },

            MASTER_FUNDING_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Limit',
            },

            MASTER_UTILISED_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Available Limit',
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Buyer.',
                  stalePeriodFromDueDate: true
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  label: 'Invoice Amount',
                  fieldType: 'TEXT_NUMBER'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Disbursement Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: false,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: true,
          isEditable: true
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            LENDING: {
              isVisible: false,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: false,
              isEditable: false,
              label: ''
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      INVOICE_FACTORING_WO_RECOURSE: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'INVOICE_FACTORING_WO_RECOURSE'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Invoice Factoring w/o Recourse - Trade Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `Invoice Factoring is a type of short-term accounts receivable financing, where you effectively 'sell' your outstanding invoices to a third-party commercial finance company. The applicant (seller) gets the cash from the financial institution based on the commercial terms agreed and the trading partner (buyer) pays back to the financial institution on the due date.`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        RECOURSE_ON: {
          isVisible: false,
          label: 'NONE'
        },
        SETTLEMENT_BY_FI: {
          isVisible: true, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'IMPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_IFWOR'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            ALLOW_MASTER_INVOICE_SELECTION: {
              isVisible: false,
              isEditable: false,
            },
            MASTER_INVOICE_RADIO: {
              isVisible: false,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICATION_NUMBER: {
              isVisible: false,
              isEditable: false,
            },
            CONVERSION_DATE: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_INTEREST: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_FEES: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_TAX: {
              isVisible: true,
              isEditable: true
            },
            MASTER_INVOICE_NO: {
              isVisible: false,
              isEditable: false,
              label: 'Master Invoice Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            MASTER_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Amount',

            },

            MASTER_FUNDING_PERCENTAGE: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Percentage',
            },

            MASTER_FUNDING_LIMIT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Limit',
            },

            MASTER_UTILISED_LIMIT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Available Limit',
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Counter Party.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  label: 'Invoice Amount',
                  fieldType: 'TEXT_NUMBER'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  label: 'Invoice Amount in',
                  fieldType: 'TEXT_NUMBER'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: false,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: true
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: false,
              isEditable: false,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            EXTRA_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            SHORT_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Currency: Amount Received From Counter Party'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Amount Received From Counter Party'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: true, //this.productType === ProductType.invoiceFactoring
              isEditable: true,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            HBL_NO: {
              isVisible: true,
              isEditable: true,
              isMandatory: true,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            LENDING: {
              isVisible: true,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: true,
              isEditable: true,
              label: 'Agreement between Financial Institution and Applicant/Counter Party'
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      INVENTORY_FINANCE_BUYER: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'LOAN_OR_ADVANCE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'INVENTORY_FINANCE_BUYER'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Inventory Finance Buyer'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: '-'
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'BUYER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: false,
          label: 'NONE'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_IVFB'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            ALLOW_MASTER_INVOICE_SELECTION: {
              isVisible: false,
              isEditable: false,
            },
            MASTER_INVOICE_RADIO: {
              isVisible: false,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICATION_NUMBER: {
              isVisible: false,
              isEditable: false,
            },
            CONVERSION_DATE: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_INTEREST: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_FEES: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_TAX: {
              isVisible: true,
              isEditable: true
            },
            MASTER_INVOICE_NO: {
              isVisible: false,
              isEditable: false,
              label: 'Master Invoice Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: false,
              isEditable: false,
              fieldType: '',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            MASTER_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Amount',

            },

            MASTER_FUNDING_PERCENTAGE: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Percentage',
            },

            MASTER_FUNDING_LIMIT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Limit',
            },

            MASTER_UTILISED_LIMIT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Available Limit',
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  tooltip: 'The due date on which the payment is expected to be received from the Counter Party.',
                  label: 'Invoice Due Date',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER'
                },
                INVOICE_COMMODITIES: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: false,
              isEditable: false
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: false,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: false,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: false,
              isEditable: false,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: true,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: false,
          isEditable: false
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: false,
          isEditable: false,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: false,
          isEditable: false,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            LENDING: {
              isVisible: false,
              isEditable: false,
            },
            NON_LENDING: {
              isVisible: false,
              isEditable: false,
              label: ''
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: false
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      INVENTORY_FINANCE_SELLER: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'LOAN_OR_ADVANCE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'INVENTORY_FINANCE_SELLER'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Inventory Finance Seller'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: '-'
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'SELLER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: false,
          label: 'NONE'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_IVFS'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            ALLOW_MASTER_INVOICE_SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            MASTER_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICATION_NUMBER: {
              isVisible: true,
              isEditable: true,
            },
            CONVERSION_DATE: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_INTEREST: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_FEES: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_TAX: {
              isVisible: true,
              isEditable: true
            },
            MASTER_INVOICE_NO: {
              isVisible: true,
              isEditable: true,
              label: 'Master Invoice Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: false,
              isEditable: false,
              fieldType: '',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            MASTER_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Amount',

            },

            MASTER_FUNDING_PERCENTAGE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Percentage',
            },

            MASTER_FUNDING_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Limit',
            },

            MASTER_UTILISED_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Available Limit',
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Counter Party.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: false,
              isEditable: false
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: false,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: false,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: false,
              isEditable: false,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: true,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: false,
          isEditable: false
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: true,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: false,
          isEditable: false,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: false,
          isEditable: false,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            LENDING: {
              isVisible: false,
              isEditable: false,
            },
            NON_LENDING: {
              isVisible: false,
              isEditable: false,
              label: ''
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: false
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      REVOLVING_CREDIT_FACILITY: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'LOAN_OR_ADVANCE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'REVOLVING_CREDIT_FACILITY'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Revolving Credit Facility'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `A working capital revolving credit facility, often referred to as a revolving credit facility, is a financial arrangement that provides a company with access to a specified amount of funds for its day-to-day operational needs. It is a type of  business financing that helps companies manage their short-term working capital requirements, such as covering operational expenses, managing inventory, or meeting short-term liabilities.`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'SELLER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'NONE'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_RCF'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: false,
              isEditable: false,
              fieldType: '',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: false,
              isEditable: false,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER'
            },
            INVOICE_DETAILS: {
              isVisible: false,
              isEditable: false,
              fieldData: {
                INVOICE_ID: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'TEXT',
                  label: ''
                },
                INVOICE_DATE: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'DATE',
                  label: ''
                },
                INVOICE_TERMS: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'TEXT_NUMBER',
                  label: ''
                },
                INVOICE_DUE_DATE: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'DATE',
                  label: '',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'TEXT_NUMBER',
                  label: ''
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: ''
                },
                INVOICE_FILE: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: false,
              isEditable: false
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: false,
                    },
                    INVOICE_DATE: {
                      isVisible: false,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: false,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: false,
              isEditable: false,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: false,
          isEditable: false
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: true,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: true,
          isEditable: true
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: true,
          isEditable: true
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: false,
          isEditable: false,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: false,
          isEditable: false,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            LENDING: {
              isVisible: false,
              isEditable: false,
            },
            NON_LENDING: {
              isVisible: false,
              isEditable: false,
              label: ''
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: false
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      REVERSE_FACTORING_SELLER: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'REVERSE_FACTORING_SELLER'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Reverse Factoring - Charges Borne by Seller'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `Reverse factoring, also referred to as supply chain finance, is a Buyer Led financing option where the supplier’s invoice is financed by a bank or financial institution at a discounted rate. Because the invoice has been sold, the supplier receives an immediate cash injection, and the buyer gets a little more time to pay the invoice. It’s essentially a three-way financing solution involving the buyer, the supplier, and the financial institution`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'IMPORTER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: true, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_RFS'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN_WITH_ADD_EDIT',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Buyer.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: false,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: true
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
              label: 'showBeneficiaryBankDetails'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: false,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            EXTRA_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            SHORT_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Currency: Amount Received From Counter Party'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Amount Received From Counter Party'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            HBL_NO: {
              isVisible: true,
              isEditable: true,
              isMandatory: true,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            LENDING: {
              isVisible: true,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: true,
              isEditable: true,
              label: 'Agreement between Financial Institution and Applicant/Counter Party'
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      MRPA: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'FI_FINANCING'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'MRPA'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'MRPA'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: 'MRPA refers to Master Risk Participation Agreement which enables a Financial Institution to participate on risk and co-lend with another Financial Institution on a funded or unfunded basis.'
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'FINANCIAL_INSTITUTION'  // SPONSOR
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'SPONSOR'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'SPONSOR' // prev value APPLICANT
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT' // prev value COUNTER_PARTY
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_MRPA'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: false,
              isEditable: false,
              fieldType: '',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Id/Ref No'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Counter Party.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
              label: ''
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: true,
              isEditable: true,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: true,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: true,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: true,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: true
        },
        TRACKING_INFORMATION: {
          isVisible: false,
          isEditable: false,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            LENDING: {
              isVisible: true,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: true,
              isEditable: true,
              label: 'Agreement between Financial Institution and Applicant'
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: false
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      MRPA_CLIENT: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'FI_FINANCING'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'MRPA_CLIENT'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'MRPA - Disbursement to Client'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: 'MRPA refers to Master Risk Participation Agreement which enables a Financial Institution to participate on risk and co-lend with another Financial Institution on a funded or unfunded basis. This product enables for cases where the funding shall be directly done to the corporates.'
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'FINANCIAL_INSTITUTION' // SPONSOR
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT' // prev value COUNTER_PARTY
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'SPONSOR'// prev value APPLICANT
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'// prev value COUNTER_PARTY
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_MRPAC'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: false,
              isEditable: false,
              fieldType: '',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Id/Ref No'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Buyer.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Disbursement Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
              label: ''
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: true,
              isEditable: true,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: false,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: true,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: true,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: true,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: true
        },
        TRACKING_INFORMATION: {
          isVisible: false,
          isEditable: false,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            LENDING: {
              isVisible: false,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: false,
              isEditable: false,
              label: ''
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: false
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      SCF_DPWFS: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'SCF_DPWFS'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'SCF by DPWFS'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: 'Supply Chain Finance, is a Buyer Led financing option where the supplier’s invoice is financed by a bank or financial institution at a discounted rate. Because the invoice has been sold, the supplier receives an immediate cash injection, and the buyer gets a little more time to pay the invoice. It’s essentially a three-way financing solution involving the buyer, the supplier, and the financial institution'
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'IMPORTER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_SCFDPWFS'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN_WITH_ADD_EDIT',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Id/Ref No'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Buyer.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Amount',
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Disbursement Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
              label: 'showBeneficiaryBankDetailsSCF'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: false,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            LENDING: {
              isVisible: false,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: false,
              isEditable: false,
              label: ''
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      ADVANCE_PAYMENT_SOLUTION_CLIENT: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'ADVANCE_PAYMENT_SOLUTION_CLIENT'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Advance Payment Solution – Disbursement to Client - Trade Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `Advance Payment Solution – Disbursement to Client - Trade Finance`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'IMPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_APSC'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            MASTER_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: false,
              isEditable: true,
            },
            MASTER_INVOICE_NO: {
              isVisible: true,
              isEditable: true,
              label: 'Master Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },

            MASTER_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Amount',

            },

            MASTER_FUNDING_PERCENTAGE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Funding Percentage',
            },

            MASTER_FUNDING_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Funding Limit',
            },

            MASTER_UTILISED_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Available Limit',
            },

            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Request Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Conversion Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Counter Party.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Request Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: false,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: false,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: false,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: false,
              isEditable: false,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: true,
              isEditable: true,
              isMandatory: true,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            LENDING: {
              isVisible: true,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: true,
              isEditable: true,
              label: 'Agreement between Financial Institution and Applicant/Counter Party'
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: false
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: false
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      ADVANCE_PAYMENT_SOLUTION_CP: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'TRADE_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'ADVANCE_PAYMENT_SOLUTION_CP'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Advance Payment Solution – Disbursement to Counter Party - Trade Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: 'The finance can be applied by buyer. This gives the buyer more time to pay while our platform ensures the sellers are paid in-time.'
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'IMPORTER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_APSCP'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            ALLOW_MASTER_INVOICE_SELECTION: {
              isVisible: false,
              isEditable: false,
            },
            MASTER_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: false,
              isEditable: true,
            },
            APPLICATION_NUMBER: {
              isVisible: false,
              isEditable: false,
            },
            CONVERSION_DATE: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_INTEREST: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_FEES: {
              isVisible: false,
              isEditable: false,
            },
            MASTER_INVOICE_NO: {
              isVisible: true,
              isEditable: true,
              label: 'Master Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN_WITH_ADD_EDIT',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            MASTER_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Amount',

            },

            MASTER_FUNDING_PERCENTAGE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Funding Percentage',
            },

            MASTER_FUNDING_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Funding Limit',
            },

            MASTER_UTILISED_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Available Limit',
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Request Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Conversion Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Buyer.',
                  stalePeriodFromDueDate: true
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  label: 'Request Amount',
                  fieldType: 'TEXT_NUMBER'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: false,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: false,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Disbursement Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: false,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: true,
          isEditable: true
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            LENDING: {
              isVisible: false,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: false,
              isEditable: false,
              label: ''
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: false
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: false
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
    },
    LOGISTICS_FINANCE: {
      INVOICE_DISCOUNTING: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'LOGISTICS_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'INVOICE_DISCOUNTING'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Invoice Discounting - Logistics Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `Invoice Discounting is a form of short-term borrowing against your outstanding invoices. The applicant (seller) gets the cash from the financial institution based on the commercial terms agreed and the applicant (seller) pays back to the financial institution on the due date.`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'SELLER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'BUYER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_ID'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN_WITH_ADD_EDIT',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Counter Party.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: false,
              isEditable: false
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: false,
              isEditable: false,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,//MAKE FALSE FOR OTHERS
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: true,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            LENDING: {
              isVisible: true,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: true,
              isEditable: true,
              label: 'Agreement between Financial Institution and Applicant'
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: false,
              isEditable: false,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      INVOICE_FACTORING: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'LOGISTICS_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'INVOICE_FACTORING'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Invoice Factoring - Logistics Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `Invoice Factoring is a type of short-term accounts receivable financing, where you effectively 'sell' your outstanding invoices to a third-party commercial finance company. The applicant (seller) gets the cash from the financial institution based on the commercial terms agreed and the trading partner (buyer) pays back to the financial institution on the due date.`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'SELLER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: true, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'BUYER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_IF'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Counter Party.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: false,
              isEditable: false
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: false,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: true
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: false,
              isEditable: false,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            EXTRA_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            SHORT_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Currency: Amount Received From Counter Party'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Amount Received From Counter Party'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            LENDING: {
              isVisible: true,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: true,
              isEditable: true,
              label: 'Agreement between Financial Institution and Applicant/Counter Party'
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      REVERSE_FACTORING: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'LOGISTICS_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'REVERSE_FACTORING'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Reverse Factoring - Logisitcs Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `The finance can be applied by buyer. This gives the buyer more time to pay while our platform ensures the sellers are paid in-time.`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'BUYER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: true, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'SELLER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_RF'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN_WITH_ADD_EDIT',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Buyer.',
                  stalePeriodFromDueDate: true
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: false,
              isEditable: false
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Disbursement Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: false,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            EXTRA_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: true,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            LENDING: {
              isVisible: false,
              isEditable: false,
            },
            NON_LENDING: {
              isVisible: false,
              isEditable: false,
              label: ''
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      TRADE_PAYABLES_FINANCING: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'LOGISTICS_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'TRADE_PAYABLES_FINANCING'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Advance Payment Solution – Disbursement to Counter Party - Logistics Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `The finance can be applied by buyer. This gives the buyer more time to pay while our platform ensures the sellers are paid in-time.`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'BUYER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'SELLER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_TP'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            ALLOW_MASTER_INVOICE_SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            MASTER_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICATION_NUMBER: {
              isVisible: true,
              isEditable: true,
            },
            CONVERSION_DATE: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_INTEREST: {
              isVisible: true,
              isEditable: true,
            },
            CARRIED_FEES: {
              isVisible: true,
              isEditable: true,
            },
            MASTER_INVOICE_NO: {
              isVisible: true,
              isEditable: true,
              label: 'Master Invoice Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN_WITH_ADD_EDIT',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            MASTER_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Amount',

            },

            MASTER_FUNDING_PERCENTAGE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Percentage',
            },

            MASTER_FUNDING_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Funding Limit',
            },
            MASTER_UTILISED_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Invoice Available Limit',
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Buyer.',
                  stalePeriodFromDueDate: true
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: false,
              isEditable: false
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Disbursement Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: false,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: true,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            LENDING: {
              isVisible: false,
              isEditable: false,
            },
            NON_LENDING: {
              isVisible: false,
              isEditable: false,
              label: ''
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      INVOICE_FACTORING_WO_RECOURSE: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'LOGISTICS_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'INVOICE_FACTORING_WO_RECOURSE'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Invoice Factoring w/o Recourse - Logistics Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `Invoice Factoring is a type of short-term accounts receivable financing, where you effectively 'sell' your outstanding invoices to a third-party commercial finance company. The applicant (seller) gets the cash from the financial institution based on the commercial terms agreed and the trading partner (buyer) pays back to the financial institution on the due date.`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'SELLER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        RECOURSE_ON: {
          isVisible: false,
          label: 'NONE'
        },
        SETTLEMENT_BY_FI: {
          isVisible: true, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'BUYER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_IFWOR'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            NORMAL_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Invoice Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Counter Party.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: false,
              isEditable: false
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: false,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: true
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: false,
              isEditable: false,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: true,//MAKE FALSE FOR OTHERS
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            EXTRA_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            SHORT_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Currency: Amount Received From Counter Party',
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Amount Received From Counter Party'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            LENDING: {
              isVisible: true,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: true,
              isEditable: true,
              label: 'Agreement between Financial Institution and Applicant/Counter Party'
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      ADVANCE_PAYMENT_SOLUTION_CLIENT: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'LOGISTICS_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'ADVANCE_PAYMENT_SOLUTION_CLIENT'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Advance Payment Solution – Disbursement to Client - Logistics Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `Advance Payment Solution – Disbursement to Client - Logistics Finance`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'EXPORTER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'APPLICANT'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'IMPORTER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_APSC'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            MASTER_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: false,
              isEditable: true,
            },
            MASTER_INVOICE_NO: {
              isVisible: true,
              isEditable: true,
              label: 'Master Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },

            MASTER_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Amount',

            },

            MASTER_FUNDING_PERCENTAGE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Funding Percentage',
            },

            MASTER_FUNDING_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Funding Limit',
            },

            MASTER_UTILISED_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master Available Limit',
            },

            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Request Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'Conversion Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Counter Party.',
                  stalePeriodFromDueDate: false
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Request Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: false,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: false,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: true,
              isEditable: true
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: false,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: true,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Receivable Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: false,
              isEditable: false,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: true,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: true,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            ALLOT_WHOLE_RECEIPT: {
              isVisible: false,
              isEditable: false,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: true,
              isEditable: true,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                CARRIED_FEES: {
                  isVisible: true,
                  isEditable: true,
                },
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: false,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: true,
              isEditable: true,
              isMandatory: true,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            LENDING: {
              isVisible: true,
              isEditable: true,
            },
            NON_LENDING: {
              isVisible: true,
              isEditable: true,
              label: 'Agreement between Financial Institution and Applicant/Counter Party'
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: false
            },
            DOCUMENT: {
              isVisible: true,
              isMandatory: false
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
      ADVANCE_PAYMENT_SOLUTION_CP: {
        PRODUCT_SUPER_CATEGORY: {
          isVisible: true,
          label: 'RECEIVABLE_PURCHASE'
        },
        PRODUCT_CATEGORY: {
          isVisible: true,
          label: 'LOGISTICS_FINANCE',
        },
        PRODUCT_TYPE: {
          isVisible: true,
          label: 'ADVANCE_PAYMENT_SOLUTION_CP'
        },
        FINANCE_TYPE: {
          isVisible: true,
          label: 'DEFERRED/IN_TRANSIT',
          fieldData: {
            DEFERRED: {
              isVisible: true,
              isEditable: true
            },
            IN_TRANSIT: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PRODUCT_NAME: {
          isVisible: true,
          label: 'Advance Payment Solution – Disbursement to Counter Party - Logistics Finance'
        },
        PRODUCT_DESCRIPTION: {
          isVisible: true,
          label: `The finance can be applied by buyer. This gives the buyer more time to pay while our platform ensures the sellers are paid in-time.`
        },
        APPLICANT_TYPE: {
          isVisible: true,
          label: 'BUYER'
        },
        DISBURSEMENT_TO: {
          isVisible: true,
          label: 'COUNTER_PARTY'
        },
        COLLECTION_FROM: {
          isVisible: true,
          label: 'APPLICANT'
        },
        RECOURSE_ON: {
          isVisible: true,
          label: 'APPLICANT'
        },
        SETTLEMENT_BY_FI: {
          isVisible: false, // means applicable for that product
        },
        COUNTERPARTY_TYPE: {
          isVisible: true,
          label: 'SELLER'
        },
        DISBURSAL_RESTRICTION_KEY: {
          isVisible: true,
          label: 'restrict_disbursal_APSCP'
        },
        OCR: {
          isVisible: true,
          isEditable: false,
          isMandatory: false
        },
        APPLICATION_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        ACCEPT_TERMS_CONTENT: {
          isVisible: true,
          isEditable: true,
        },
        APPLICANT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            ALLOW_MASTER_INVOICE_SELECTION: {
              isVisible: false,
              isEditable: false,
            },
            MASTER_INVOICE_RADIO: {
              isVisible: true,
              isEditable: true,
            },
            NORMAL_INVOICE_RADIO: {
              isVisible: false,
              isEditable: true,
            },
            APPLICATION_NUMBER: {
              isVisible: false,
              isEditable: false,
            },
            CONVERSION_DATE: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_PRINCIPAL: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_INTEREST: {
              isVisible: false,
              isEditable: false,
            },
            CARRIED_FEES: {
              isVisible: false,
              isEditable: false,
            },
            MASTER_INVOICE_NO: {
              isVisible: false,
              isEditable: false,
              label: 'Master PO Number',
            },
            APPLICANT: {
              isVisible: true,
              isEditable: true,
            },
            FINANCIAL_INSTITUTION: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            FINANCING_TYPE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            COUNTER_PARTY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'SEARCHABLE_DROP_DOWN_WITH_ADD_EDIT',
            },
            DISBURSEMENT_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            INVOICE_CURRENCY: {
              isVisible: true,
              isEditable: true,
              fieldType: 'DROP_DOWN'
            },
            APPLICATION_AMOUNT: {
              isVisible: false,
              isEditable: false,
              fieldType: 'TEXT_NUMBER'
            },
            MASTER_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master PO Amount',
            },
            MASTER_FUNDING_PERCENTAGE: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master PO Funding Percentage',
            },
            MASTER_FUNDING_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master PO Funding Limit',
            },

            MASTER_UTILISED_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master PO Utilised Limit',
            },

            MASTER_AVAILABLE_LIMIT: {
              isVisible: true,
              isEditable: true,
              fieldType: 'TEXT_NUMBER',
              label: 'Master PO Available Limit',
            },

            INVOICE_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INVOICE_ID: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'TEXT',
                  label: 'Invoice Id'
                },
                INVOICE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'PO Request Date'
                },
                INVOICE_TERMS: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'PO Terms (days)'
                },
                INVOICE_DUE_DATE: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DATE',
                  label: 'PO Conversion Due Date',
                  tooltip: 'The due date on which the payment is expected to be received from the Buyer.',
                  stalePeriodFromDueDate: true
                },
                INVOICE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'TEXT_NUMBER',
                  label: 'PO Request Amount'
                },
                INVOICE_AMOUNT_CURRENCY: {
                  isVisible: false,
                  isEditable: false,
                  fieldType: 'TEXT_NUMBER',
                  label: 'Invoice Amount in'
                },
                INVOICE_FILE: {
                  isVisible: true,
                  isEditable: false,
                  fieldType: 'FILE'
                },
                INVOICE_COMMODITIES: {
                  isVisible: true,
                  isEditable: true,
                  fieldType: 'DROP_DOWN'
                },
                ADD_INVOICE: {
                  isVisible: true,
                  isEditable: false,
                  fieldType: 'BUTTON'
                }
              }
            },
          }
        },
        INVOICE_CF_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TOTAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false
            }
          }
        },
        SOFR_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            TENURE: {
              isVisible: true,
              isEditable: true
            },
            DATE: {
              isVisible: true,
              isEditable: true
            },
            RATE: {
              isVisible: true,
              isEditable: true
            },
            MARGIN: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        DISBURSEMENT_TERMS_REQUEST: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BL_DATE: {
              isVisible: false,
              isEditable: false
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: { // comparison: 'MORE_THAN',
                  fieldData: {
                    CURRENT_DATE: {
                      isVisible: true,
                    },
                    BL_DATE: {
                      isVisible: true,
                    },
                    INVOICE_DATE: {
                      isVisible: true,
                    }
                  }
                },
                max: { // comparison: 'LESS_THAN',
                  fieldData: {
                    BL_DATE_PLUS_X_DAYS: {
                      isVisible: true,
                    },
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    }
                  }
                }
              }
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
              conditions: {
                max: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_FINANCING_TENURE']
                }
              }
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                equal: {
                  fieldData: {
                    INVOICE_DUE_DATE: {
                      isVisible: false,
                    },
                    DISBURSAL_DATE_PLUS_MAX_FINANCING_TENURE: {
                      isVisible: true
                    }
                  }
                }
              },
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                min: {
                  fieldData: {
                    SUM_OF_RECEIVABLE_PROCESSING_DISCOUNT_FEE_AMOUNTS: {
                      isVisible: false
                    }
                  }
                }
              },
              conditions: {
                min: {
                  comparison: 'LESS_THAN_OR_EQUAL_TO',
                  comparisonSymbol: '<=',
                  fieldsAppliedTo: ['MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
                equal: {
                  comparison: 'DIVISION',
                  comparisonSymbol: '/',
                  fieldsAppliedTo: ['DISBURSAL_AMOUNT', 'INVOICE_AMOUNT']
                }
              }
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true,
              conditions: {
                equal: {
                  comparison: 'MULTIPLY',
                  comparisonSymbol: '*',
                  fieldsAppliedTo: ['INVOICE_AMOUNT', 'MAX_SANCTIONED_AMOUNT_PERCENTAGE']
                },
              },
              label: 'Disbursement Amount'
            },
            INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            TRANSACTION_FEE: {
              isVisible: true,
              isEditable: true,
            },
            FACTORING_FEE: {
              isVisible: false,
              isEditable: false,
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            BENEFICIARY_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
            },
            CP_BANK_DETAILS: {
              isVisible: true,
              isEditable: true,
              label: 'counter_party_bank_details_required'
            },
            FEE_AMOUNTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        REQUEST_TERMS_DETAILS: {
          isVisible: false,
        },
        DISBURSEMENT_TERMS_APPROVED: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            BENCHMARK: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SANCTIONED_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            DISBURSAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FIXED_INTEREST_RATE: {
              isVisible: true,
              isEditable: true,
            },
            REVISED_DUE_DATE: {
              isVisible: true,
              isEditable: true
            },
            REASON_FOR_DELAY: {
              isVisible: true,
              isEditable: true
            },
            ALLOW_CUSTOM_INTEREST_RATE: {
              isVisible: false,
              isEditable: false
            },
            UPDATED_DISBURSAL_AND_DUE_DATE: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                ACTUAL_DISBURSAL_DATE: {
                  isVisible: true,
                  isEditable: true
                },
                UPDATED_DUE_DATE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            }
          }
        },
        APPLICATION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HEADING: {
              isVisible: true,
              isEditable: false,
            },
            PROCESS: {
              isVisible: true,
              isEditable: false,
            },
            DETAIL: {
              isVisible: true,
              isEditable: false,
            },
          }
        },
        ACCEPTED_TERMS: {
          isVisible: true,
          isEditable: true,
        },
        ACTUAL_TRANSACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FINANCING_TENURE: {
              isVisible: true,
              isEditable: true,
            },
            REQUESTED_AMOUNT_APPROVED: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHARGES_TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_CHANGES_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                VIEW_TRANSACTION_BTN: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            BALANCES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PAYMENT_MADE_BY_BUYER: {
                  isVisible: false,
                  isEditable: true,
                },
                EXTRA_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_PAYMENT: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMOUNT: {
                  isVisible: false,
                  isEditable: false,
                },
              }
            },
            WAIVER_COMMENTS: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        APPLICANT_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        COUNTER_PARTY_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FI_BANK_DETAILS: {
          isVisible: true,
          isEditable: true
        },
        FEE_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            INTEGRATED_FEES: {
              isVisible: true,
              isEditable: true
            },
            INTEREST_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            TRANSACTION_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            FACTORING_FEE_AMOUNT: {
              isVisible: false,
              isEditable: false
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                AMOUNT: {
                  isVisible: true,
                  isEditable: true
                },
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true
                }
              }
            },
            OVERDUE_FEE_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SETTLEMENT_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            ANNUAL_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            ENHANCEMENT_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            },
            RENEW_PROCESSING_FEES: {
              isVisible: true,
              isEditable: true
            }
          }
        },
        PAYMENT_SIMULATOR: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SETTLEMENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            PRE_PAYMENT_CHARGES: {
              isVisible: true,
              isEditable: true
            },
            PRINCIPAL_AMOUNT: {
              isVisible: true,
              isEditable: true
            },
            SIMULATOR_FIELDS: {
              isVisible: true,
              isEditable: true,
            },
            SETTLEMENT_PREVIEW_ADVICE: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        TRANSACTION_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_SUB_TYPE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            EVENT_DATE: {
              isVisible: true,
              isEditable: true,
            },
            UPDATED_DUE_DATE: {
              isVisible: true,
              isEditable: true,
            },
            ACTUAL_DISBURSAL_DATE: {
              isVisible: true,
              isEditable: true,
            },
            OVERDUE_FEE_AMT: {
              isVisible: true,
              isEditable: true,
            },
            OTHER_CHARGES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TYPE: {
                  isVisible: true,
                  isEditable: true,
                },
                AMOUNT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            INTEREST_RATE_APPLICABLE: {
              isVisible: true,
              isEditable: true,
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            SOFR_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TENURE: {
                  isVisible: true,
                  isEditable: true,
                },
                DATE: {
                  isVisible: true,
                  isEditable: true,
                },
                RATE: {
                  isVisible: true,
                  isEditable: true,
                },
                MARGIN: {
                  isVisible: true,
                  isEditable: true,
                },
              }
            }
          }
        },
        SUPPORTING_DOCUMENT: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            DOWNLOAD: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        BANK_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            DEBIT_FROM: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_TO: {
              isVisible: true,
              isEditable: false,
            },
            DEBIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            CREDIT_ACCOUNT_NO: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_MODE: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_BY: {
              isVisible: true,
              isEditable: true,
            },
            MARGIN_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            PAYMENT_REF_NO: {
              isVisible: true,
              isEditable: true,
            },
            REMARKS: {
              isVisible: true,
              isEditable: true,
            },
            FEES: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            }
          }
        },
        SETTLEMENT_DETAILS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            CURRENCY: {
              isVisible: true,
              isEditable: true,
            },
            TOTAL_INVOICE_AMT: {
              isVisible: false,
              isEditable: false,
            },
            AMOUNT_RECEIVED_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            PRINCIPAL_AMT: {
              isVisible: true,
              isEditable: true,
            },
            UPFRONT_FEE: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_AMTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                PRINCIPAL: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX: {
                  isVisible: false,
                  isEditable: false,
                },
                BALANCE_CARRIED_FEES: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            TAX_AMOUNT: {
              isVisible: true,
              isEditable: true,
            },
            FEE_ON_BALANCE_PRINCIPAL: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                TRANSACTION_FEE: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST: {
                  isVisible: true,
                  isEditable: true,
                },
                MASTER_CARRIED_FEES: {
                  isVisible: false,
                  isEditable: false,
                }
              }
            },
            OTHER_FEE: {
              isVisible: false,
              isEditable: false,
            },
            TOTAL_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            BALANCE_INVOICE_AMOUNT: {
              isVisible: false,
              isEditable: false,
            },
            EXTRA_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            PENDING_CUSTOMER_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            BALANCE_AMOUNT_TO_CUSTOMER: {
              isVisible: false,
              isEditable: false,
            },
            CURRENCY_AMOUNT_TO_BE_RECEIVED: {
              isVisible: false,
              isEditable: false,
              label: 'Currency: Amount Received From Customer'
            },
            AMOUNT_TO_BE_RECEIVED: {
              isVisible: true,
              isEditable: true,
              label: 'Amount Received From Customer'
            },
            SPOT_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_RATE: {
              isVisible: false,
              isEditable: false,
            },
            FORWARD_REFERENCE_ID: {
              isVisible: false,
              isEditable: false,
            },
            TAX_DETAILS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                RATE_CODE: {
                  isVisible: true,
                  isEditable: true,
                },
                REGIME_CODE: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        TRANSACTION_EVENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            EVENT: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT: {
              isVisible: true,
              isEditable: true,
            },
            AMOUNT_FROM_CP: {
              isVisible: false,
              isEditable: false,
            },
            SETTLE_AMT_AGAINST: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                TRNX_FEE_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                TAX_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                FACTORING_FEE_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                FACTORING_FEE_TAX_AMT: {
                  isVisible: false,
                  isEditable: false,
                },
                OVERDUE_INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                OTHER_FEE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                INTEREST_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                PRINCIPAL_AMT: {
                  isVisible: true,
                  isEditable: true,
                },
                EXTRA_BALANCE: {
                  isVisible: true,
                  isEditable: true,
                },
                SHORT_BALANCE_SETTLED: {
                  isVisible: true,
                  isEditable: true,
                },
                WAIVER_COMMENTS: {
                  isVisible: true,
                  isEditable: true,
                  fieldData: {
                    OPERATIONS: {
                      isVisible: true,
                      isEditable: true,
                    },
                    CREDIT_ANALYST: {
                      isVisible: true,
                      isEditable: true,
                    }
                  }
                }
              }
            }
          }
        },
        PREVIOUS_SHORT_PAYMENT: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            APPLICATION_NO: {
              isVisible: true,
              isEditable: true,
            },
            SHORT_PAYMENT_AMT: {
              isVisible: true,
              isEditable: true,
            },
            MONEY_TO_BE_REMITTED: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        STATUS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            SELECTION: {
              isVisible: true,
              isEditable: true,
            },
            INTERNAL_COMMENTS: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        CUSTOM_INTEREST_RATE: {
          isVisible: false,
          isEditable: false
        }, UPCOMING_INTEREST_DUE_DATE: {
          isVisible: false,
          isEditable: false
        },
        CREDIT_NOTE: {
          isVisible: true,
          isEditable: true
        },
        DPWFS_COMMISSION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            FINANCE_COMMISSION_AMT: {
              isVisible: true,
              isEditable: false
            },
            TRANSACTION_FEE_AMT: {
              isVisible: true,
              isEditable: false
            },
            MISC_FEES_COMMISSION: {
              isVisible: true,
              isEditable: false
            }
          }
        },
        FLAGS: {
          isVisible: true,
          isEditable: true
        },
        MARGIN_AMOUNT: {
          isVisible: true,
          isEditable: false
        },
        TRACKING_INFORMATION: {
          isVisible: true,
          isEditable: true,
          isMandatory: false,
          fieldData: {
            HBL_NO: {
              isVisible: false,
              isEditable: false,
              isMandatory: false,
            },
          }
        },
        FI_APPROVAL_DOCS: {
          isVisible: true,
          isEditable: true,
        },
        ASSIGNMENT_OF_RECEIVABLES: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            DOCUSIGN_BTN: {
              isVisible: false,
              isEditable: false,
            }
          }
        },
        ASSIGNMENT_OF_RECEIVABLES_VIEW: {
          isVisible: true,
          isEditable: true,
        },
        AGREEMENT_DOCUMENT: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            LENDING: {
              isVisible: false,
              isEditable: false,
            },
            NON_LENDING: {
              isVisible: false,
              isEditable: false,
              label: ''
            }
          }
        },
        TRNX_MONITORING: {
          isVisible: false,
          isEditable: false,
          fieldData: {
            RESPONSE_TABLE: {
              isVisible: true,
              isEditable: true,
            },
            COUNTERPARTY_FIELDS: {
              isVisible: true,
              isMandatory: true
            }
          }
        },
        FI_TERMS_VALIDATION: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            COMMENTS: {
              isVisible: true,
              isEditable: true,
            },
            DOCUMENTS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                DOWNLOAD: {
                  isVisible: true,
                  isEditable: true,
                },
                UPLOAD: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            },
            ACTIONS: {
              isVisible: true,
              isEditable: true,
              fieldData: {
                HISTORY: {
                  isVisible: true,
                  isEditable: true,
                },
                APPROVE: {
                  isVisible: true,
                  isEditable: true,
                },
                REJECT: {
                  isVisible: true,
                  isEditable: true,
                }
              }
            }
          }
        },
        ADVICE_DOCUMENTS: {
          isVisible: true,
          isEditable: true
        },
        HISTORY_INFORMATION: {
          isVisible: true,
          isEditable: true
        },
        WAIVE_OFF_OVERDUE_ACTIONS: {
          isVisible: true,
          isEditable: true,
          fieldData: {
            HISTORY: {
              isVisible: true,
              isEditable: true,
            },
            APPROVE: {
              isVisible: true,
              isEditable: true,
            },
            REJECT: {
              isVisible: true,
              isEditable: true,
            },
            WAIVE_OFF_OVERDUE_INTEREST: {
              isVisible: true,
              isEditable: true,
            }
          }
        },
        DUE_DATE_ACTIONS: {
          isVisible: true,
          isEditable: true,
        },
        REMARKS: {
          isVisible: true,
          isEditable: true
        }
      },
    }
  };


  private creditFacilityProductConfiguration: CreditFacilityConfig = {
    INVOICE_DISCOUNTING: {
      commonConfig: {
        isBuyer: true,
        isSeller: false,
        allProductTypeCategoryFinanceCombos:
          ['LOGISTICS_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT',
            'LOGISTICS_FINANCE_INVOICE_DISCOUNTING_DEFERRED',
            'TRADE_FINANCE_INVOICE_DISCOUNTING_IN_TRANSIT',
            'TRADE_FINANCE_INVOICE_DISCOUNTING_DEFERRED'
          ],
        isCategoryEnabled: true
      },
      applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_ID',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: true,
        restrictForFI: true,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.COMPACT,
        fullFormTypeAllowed: true,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.FULL,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_ID',
        termsheetRequired: false,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: false,
        supplierAgreement: false
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: true,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.CREDITPOLICY,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.ONSETTLEMENT,
          collectedFrom: CollectedFrom.APPLICANT,
        },
      }
    },
    INVOICE_FACTORING: {
      commonConfig: {
        isBuyer: true,
        isSeller: false,
        allProductTypeCategoryFinanceCombos:
          ['LOGISTICS_FINANCE_INVOICE_FACTORING_IN_TRANSIT',
            'LOGISTICS_FINANCE_INVOICE_FACTORING_DEFERRED',
            'TRADE_FINANCE_INVOICE_FACTORING_IN_TRANSIT',
            'TRADE_FINANCE_INVOICE_FACTORING_DEFERRED'
          ],
        isCategoryEnabled: true
      },
      applyConfig: {
        captureLimitRequired: false,
        restrictDisbursalKeyName: '', // this is not applicable for IF
        assignmentOfReceivablesApplicable: true,
        counterPartyAcceptanceRequired: true,
        bankDetails: true,
        restrictForFI: true,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.FULL,
        fullFormTypeAllowed: true,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.FULL,
      },
      approveConfig: {
        captureLimitRequired: false,
        restrictDisbursalKeyName: '', // this is not applicable for IF
        termsheetRequired: true,
        assignmentOfReceivablesApplicable: true,
        allowCPDeletion: true,
        supplierAgreement: false
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: false,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: true,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.CREDITPOLICY,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.ONSETTLEMENT,
          collectedFrom: CollectedFrom.APPLICANT,
        },
      }
    },
    REVERSE_FACTORING: {
      commonConfig: {
        isBuyer: false,
        isSeller: true,
        allProductTypeCategoryFinanceCombos:
          ['LOGISTICS_FINANCE_REVERSE_FACTORING_IN_TRANSIT',
            'LOGISTICS_FINANCE_REVERSE_FACTORING_DEFERRED',
            'TRADE_FINANCE_REVERSE_FACTORING_IN_TRANSIT',
            'TRADE_FINANCE_REVERSE_FACTORING_DEFERRED'
          ],
        isCategoryEnabled: true
      },
      applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_RF',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: true,
        restrictForFI: true,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.COMPACT,
        fullFormTypeAllowed: false,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.COMPACT,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_RF',
        termsheetRequired: true,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: true,
        supplierAgreement: true
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: false,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.CREDITPOLICY,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.ONSETTLEMENT,
          collectedFrom: CollectedFrom.APPLICANT,
        },
      }
    },
    TRADE_PAYABLES_FINANCING: {
      commonConfig: {
        isBuyer: false,
        isSeller: true,
        allProductTypeCategoryFinanceCombos:
          ['LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT',
            'LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED',
            'TRADE_FINANCE_TRADE_PAYABLES_FINANCING_IN_TRANSIT',
            'TRADE_FINANCE_TRADE_PAYABLES_FINANCING_DEFERRED'
          ],
        isCategoryEnabled: true
      },
      applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_TP',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: true,
        restrictForFI: true,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.COMPACT,
        fullFormTypeAllowed: false,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.COMPACT,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_TP',
        termsheetRequired: true,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: true,
        supplierAgreement: false
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: false,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.CREDITPOLICY,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.ONSETTLEMENT,
          collectedFrom: CollectedFrom.APPLICANT,
        },
      }
    },
    INVOICE_FACTORING_WO_RECOURSE: {
      commonConfig: {
        isBuyer: true, isSeller: false,
        allProductTypeCategoryFinanceCombos:
          ['LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT',
            'LOGISTICS_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED',
            'TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_IN_TRANSIT',
            'TRADE_FINANCE_INVOICE_FACTORING_WO_RECOURSE_DEFERRED'
          ],
        isCategoryEnabled: true
      },
      applyConfig: {
        captureLimitRequired: false,
        restrictDisbursalKeyName: '', // this is not applicable for IF
        assignmentOfReceivablesApplicable: true,
        counterPartyAcceptanceRequired: true,
        bankDetails: true,
        restrictForFI: true,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.FULL,
        fullFormTypeAllowed: true,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.FULL,
      },
      approveConfig: {
        captureLimitRequired: false,
        restrictDisbursalKeyName: '',// this is not applicable for IF
        termsheetRequired: true,
        assignmentOfReceivablesApplicable: true,
        allowCPDeletion: true,
        supplierAgreement: false
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: false,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: true,
        factoringFee: true,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.CREDITPOLICY,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.ONSETTLEMENT,
          collectedFrom: CollectedFrom.APPLICANT,
        },
      }
    },
    INVENTORY_FINANCE_BUYER: {
      commonConfig: {
        isBuyer: true,
        isSeller: false,
        allProductTypeCategoryFinanceCombos: [],
        isCategoryEnabled: false
      },
      applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_IVFB',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: true,
        restrictForFI: true,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.NOT_APPLICABLE,
        fullFormTypeAllowed: false,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.NOT_APPLICABLE,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_IVFB',
        termsheetRequired: true,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: true,
        supplierAgreement: true
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: false,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.CREDITPOLICY,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.ONSETTLEMENT,
          collectedFrom: CollectedFrom.APPLICANT,
        },
      }
    },
    INVENTORY_FINANCE_SELLER: {
      commonConfig: {
        isBuyer: true,
        isSeller: false,
        allProductTypeCategoryFinanceCombos: [],
        isCategoryEnabled: false
      },
      applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_IVFS',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: true,
        restrictForFI: true,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.NOT_APPLICABLE,
        fullFormTypeAllowed: false,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.NOT_APPLICABLE,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_IVFS',
        termsheetRequired: true,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: true,
        supplierAgreement: true
      }, creditLimitTermsConfig: {
        minCreditPeriodVisible: false,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.CREDITPOLICY,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.ONSETTLEMENT,
          collectedFrom: CollectedFrom.APPLICANT,
        },
      }
    },
    REVOLVING_CREDIT_FACILITY: {
      commonConfig: {
        isBuyer: true,
        isSeller: false,
        allProductTypeCategoryFinanceCombos: [],
        isCategoryEnabled: false
      },
      applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_RCF',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: true,
        restrictForFI: false,
        isRiskObligorSelf: true
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.NOT_APPLICABLE,
        fullFormTypeAllowed: false,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.NOT_APPLICABLE,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_RCF',
        termsheetRequired: true,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: true,
        supplierAgreement: true
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: false,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.CREDITPOLICY,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.ONSETTLEMENT,
          collectedFrom: CollectedFrom.APPLICANT,
        },
      }
    },
    REVERSE_FACTORING_SELLER: {
      commonConfig: {
        isBuyer: true,
        isSeller: false,
        allProductTypeCategoryFinanceCombos:
          ['TRADE_FINANCE_REVERSE_FACTORING_SELLER_IN_TRANSIT',
            'TRADE_FINANCE_REVERSE_FACTORING_SELLER_DEFERRED',
          ],
        isCategoryEnabled: false
      },
      applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_RFS',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: true,
        restrictForFI: true,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.COMPACT,
        fullFormTypeAllowed: false,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.COMPACT,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_RFS',
        termsheetRequired: false,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: true,
        supplierAgreement: true
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: false,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: true,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.CREDITPOLICY,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.ONSETTLEMENT,
          collectedFrom: CollectedFrom.COUNTERPARTY,
        },
      }
    },
    MRPA: {
      commonConfig: {
        isBuyer: true,
        isSeller: false,
        allProductTypeCategoryFinanceCombos:
          ['TRADE_FINANCE_MRPA_DEFERRED',
            'TRADE_FINANCE_MRPA_IN_TRANSIT'
          ],
        isCategoryEnabled: false
      },
      applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_MRPA',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: false,
        restrictForFI: false,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.NOT_APPLICABLE,
        fullFormTypeAllowed: false,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.NOT_APPLICABLE,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_MRPA',
        termsheetRequired: false,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: false,
        supplierAgreement: false
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: true,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: true,
          riskParticipationValue: true,
          riskParticipant: true,
          riskOriginator: true,
          participationType: true,
        },
        interestRateConfigurable: InterestRateConfigurableType.FLEXIBLE,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.FLEXIBLE,
          collectedFrom: CollectedFrom.APPLICANT,
        },
      }
    },
    MRPA_CLIENT: {
      commonConfig: {
        isBuyer: false,
        isSeller: true,
        allProductTypeCategoryFinanceCombos:
          ['TRADE_FINANCE_MRPA_CLIENT_IN_TRANSIT',
            'TRADE_FINANCE_MRPA_CLIENT_DEFERRED'
          ],
        isCategoryEnabled: false
      }, applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_MRPAC',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: false,
        restrictForFI: false,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.NOT_APPLICABLE,
        fullFormTypeAllowed: false,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.NOT_APPLICABLE,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_MRPAC',
        termsheetRequired: true,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: true,
        supplierAgreement: false
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: false,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: true,
          riskParticipationValue: true,
          riskParticipant: true,
          riskOriginator: true,
          participationType: true,
        },
        interestRateConfigurable: InterestRateConfigurableType.FLEXIBLE,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.FLEXIBLE,
          collectedFrom: CollectedFrom.FLEXIBLE,
        },
      }
    },
    SCF_DPWFS: {
      commonConfig: {
        isBuyer: false,
        isSeller: true,
        allProductTypeCategoryFinanceCombos:
          ['TRADE_FINANCE_SCF_DPWFS_IN_TRANSIT',
            'TRADE_FINANCE_SCF_DPWFS_DEFERRED'
          ],
        isCategoryEnabled: false
      },
      applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_SCFDPWFS',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: true,
        restrictForFI: true,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.COMPACT,
        fullFormTypeAllowed: false,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.COMPACT,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_SCFDPWFS',
        termsheetRequired: true,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: true,
        supplierAgreement: false
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: false,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.FLEXIBLE,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.FLEXIBLE,
          collectedFrom: CollectedFrom.FLEXIBLE,
        },
      }
    },
    ADVANCE_PAYMENT_SOLUTION_CLIENT: {
      commonConfig: {
        isBuyer: true,
        isSeller: false,
        allProductTypeCategoryFinanceCombos:
          ['LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT',
            'LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED',
            'TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_IN_TRANSIT',
            'TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CLIENT_DEFERRED',
          ],
        isCategoryEnabled: true
      },
      applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_APSC',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: true,
        restrictForFI: true,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.FULL,
        fullFormTypeAllowed: true,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.FULL,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_APSC',
        termsheetRequired: false,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: false,
        supplierAgreement: false
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: true,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.CREDITPOLICY,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.ONSETTLEMENT,
          collectedFrom: CollectedFrom.APPLICANT,
        },
      }
    },
    ADVANCE_PAYMENT_SOLUTION_CP: {
      commonConfig: {
        isBuyer: false,
        isSeller: true,
        allProductTypeCategoryFinanceCombos:
          ['LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT',
            'LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED',
            'TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_IN_TRANSIT',
            'TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP_DEFERRED'
          ],
        isCategoryEnabled: true
      },
      applyConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_APSCP',
        assignmentOfReceivablesApplicable: false,
        counterPartyAcceptanceRequired: false,
        bankDetails: true,
        restrictForFI: true,
        isRiskObligorSelf: false
      },
      counterPartyComponentsConfig: {
        counterPartyFormType: CreditFacilityCounterPartyFormType.COMPACT,
        fullFormTypeAllowed: false,
        apsCounterPartyFormType: CreditFacilityCounterPartyFormType.COMPACT,
      },
      approveConfig: {
        captureLimitRequired: true,
        restrictDisbursalKeyName: 'restrict_disbursal_APSCP',
        termsheetRequired: true,
        assignmentOfReceivablesApplicable: false,
        allowCPDeletion: true,
        supplierAgreement: false
      },
      creditLimitTermsConfig: {
        minCreditPeriodVisible: false,
        maxFinancingTenureOptionsProductCategoryList: ['TRADE_FINANCE'],
        importerAcceptanceValidatorRequired: false,
        factoringFee: false,
        advanceFacilityConditions: {
          riskParticipationType: false,
          riskParticipationValue: false,
          riskParticipant: false,
          riskOriginator: false,
          participationType: false,
        },
        interestRateConfigurable: InterestRateConfigurableType.CREDITPOLICY,
        feeAndChargesCondition: {
          collectedAt: CollectedAt.ONSETTLEMENT,
          collectedFrom: CollectedFrom.APPLICANT,
        },
      }
    },
  };


  getAllProductListForCreditFacilityConfig(): string[] {
    return Object.keys(this.creditFacilityProductConfiguration);
  }

  getGivenKeyValueForCreditFacilityConfig(productType: string, configType: string, requiredKey: string, childKeyArr?: any[]): any {
    let returnVal = this.creditFacilityProductConfiguration[productType]?.[configType]?.[requiredKey];
    for (let i = 0; i < childKeyArr?.length; i++) {
      if (returnVal?.[childKeyArr[i]]) returnVal = returnVal[childKeyArr[i]];
      else return;
    }
    return returnVal;
  }

  checkProductConditionVisible(productFinanceTypeArr: any[], condition: string, expectedValue: any, expectedindex?: any): boolean {
    for (let i = 0; i < productFinanceTypeArr.length; i++) {
      const productType = this.getProductTypeAbbreviationSnakeUC(productFinanceTypeArr[i]);
      const tempValues = Object.values(this.getGivenKeyValueForCreditFacilityConfig(productType, 'creditLimitTermsConfig', condition));
      if (expectedindex !== undefined) {
        if (tempValues[expectedindex] === expectedValue) return true;
      } else {
        if (tempValues.findIndex(val => val === expectedValue) !== -1) return true;
      }
    }
    return false;
  }

  getAllProductCategoryList(): string[] {
    return Object.keys(this.conditionMapConfig.productCategory);
  }

  getAllFinanceTypeList(): string[] {
    return Object.keys(this.conditionMapConfig.financingType);
  }

  combineProductCategoryTypeAndFinancing(productCategory: string, productType: string, financingType: string): string {
    return productCategory + '_' + productType + '_' + financingType;
  }
  combineProductCategoryAndProductType(productCategory: string, productType: string): string {
    if (!productCategory) {
      productCategory = 'TRADE_FINANCE'
    } else {
      productCategory = this.getCategoryAbbreviationSnakeUC(productCategory);
    }
    productType = this.getProductTypeAbbreviationSnakeUC(productType);
    return productCategory + '_' + productType;
  }

  replaceSpecialCharacterWithSpace(inputString: string, separator: string): string {
    return inputString.replace(/_+/g, ' ');
  }

  firstLetterCaptial(inputString: string, separator: string): string {
    return inputString
      .toLowerCase()
      .split(separator)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // LC stands for lower case
  getCategoryAbbreviationLC(category: any): string {
    if (category !== null && category !== undefined && category !== '') {
      if (typeof (category) === 'number') {
        return category === 0 ? 'trade' : 'logistics';
      } else {
        return (category === '2' || category.toLowerCase().includes('logistics')) ? 'logistics' : 'trade';
      }
    }
    return '';
  }

  // UC stands for upper case
  getCategoryAbbreviationSnakeUC(category: any): string {
    if (category !== null && category !== undefined && category !== '') {
      if (typeof (category) === 'number') {
        return category === 0 ? 'TRADE_FINANCE' : 'LOGISTICS_FINANCE';
      } else {
        return (category === '2' || category.toLowerCase().includes('logistics')) ? 'LOGISTICS_FINANCE' : 'TRADE_FINANCE';
      }
    }
    return '';
  }

  // LC stands for lower case
  getCategoryAbbreviationSnakeLC(category: any): string {
    const categoryLC = this.getCategoryAbbreviationSnakeUC(category);
    return categoryLC ? categoryLC.toLowerCase() : '';
  }

  getCategoryEnum(category: any): number {
    const categorySnakeUC = this.getCategoryAbbreviationSnakeUC(category);
    return categorySnakeUC.includes('TRADE') ? ProductCategory.Trade : ProductCategory.Logistics;
  }

  getCategoryNumInStrFormat(category: any): string {
    const categorySnakeUC = this.getCategoryAbbreviationSnakeUC(category);
    return categorySnakeUC.includes('TRADE') ? '1' : '2';
  }

  getCategoryFirstLetterCaptial(category: any): string {
    if (category === '' || category === null || category === undefined) {
      return '';
    }
    const categorySnakeUC = this.getCategoryAbbreviationSnakeUC(category);
    return this.firstLetterCaptial(categorySnakeUC, '_');
  }

  // CC stands for camel case => 'TradeFinance'
  getCategoryFirstLetterCapCC(value: string): string {
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    const firstLetterCap = this.getCategoryFirstLetterCaptial(value);
    return firstLetterCap.replace(/_/g, '');
  }


  // UC stands for upper case
  getProductTypeAbbreviationSnakeUC(value: string | number): string {
    const normalizedValue = String(value).replace(/ |_|/g, '').toLowerCase();

    switch (true) {
      case typeof value === 'string' && value === '0':
        return 'All';

      case (typeof value === 'string' && value === '1') ||
        (typeof value === 'number' && value === 0) ||
        normalizedValue.includes('invoicediscounting'):
        return 'INVOICE_DISCOUNTING';

      case (typeof value === 'string' && value === '5') ||
        (typeof value === 'number' && value === 4) ||
        normalizedValue.includes('invoicefactoringworecourse') ||
        normalizedValue.includes('invoicefactoringw/orecourse'):
        return 'INVOICE_FACTORING_WO_RECOURSE';

      case (typeof value === 'string' && value === '2') ||
        (typeof value === 'number' && value === 1) ||
        normalizedValue.includes('invoicefactoring'):
        return 'INVOICE_FACTORING';

      case (typeof value === 'string' && value === '9') ||
        (typeof value === 'number' && value === 8) ||
        (normalizedValue.includes('reverse') && normalizedValue.includes('seller')):
        return 'REVERSE_FACTORING_SELLER';

      case (typeof value === 'string' && value === '3') ||
        (typeof value === 'number' && value === 2) ||
        normalizedValue.includes('reversefactoring'):
        return 'REVERSE_FACTORING';

      case (typeof value === 'string' && value === '4') ||
        (typeof value === 'number' && value === 3) ||
        normalizedValue.includes('tradepayables'):
        return 'TRADE_PAYABLES_FINANCING';

      case (typeof value === 'string' && value === '6') ||
        (typeof value === 'number' && value === 5) ||
        normalizedValue.includes('inventoryfinancebuyer'):
        return 'INVENTORY_FINANCE_BUYER';

      case (typeof value === 'string' && value === '7') ||
        (typeof value === 'number' && value === 6) ||
        normalizedValue.includes('inventoryfinanceseller'):
        return 'INVENTORY_FINANCE_SELLER';

      case (typeof value === 'string' && value === '11') ||
        (typeof value === 'number' && value === 10) ||
        (normalizedValue.includes('mrpa') && normalizedValue.includes('client')):
        return 'MRPA_CLIENT';

      case (typeof value === 'string' && value === '10') ||
        (typeof value === 'number' && value === 9) ||
        normalizedValue.includes('mrpa'):
        return 'MRPA';

      case (typeof value === 'string' && value === '12') ||
        (typeof value === 'number' && value === 11) ||
        (normalizedValue.includes('scf') && normalizedValue.includes('dpwfs')):
        return 'SCF_DPWFS';

      case (typeof value === 'string' && value === '13') ||
        (typeof value === 'number' && value === 12) ||
        (normalizedValue.includes('client') && (normalizedValue.includes('advancepayment') || normalizedValue.includes('advance_payment'))):
        return 'ADVANCE_PAYMENT_SOLUTION_CLIENT';

      case (typeof value === 'string' && value === '14') ||
        (typeof value === 'number' && value === 13) ||
        ((normalizedValue.includes('advancepayment') || normalizedValue.includes('advance_payment')) && (normalizedValue.includes('counter') || normalizedValue.includes('cp'))):
        return 'ADVANCE_PAYMENT_SOLUTION_CP';

      default:
        return 'REVOLVING_CREDIT_FACILITY';
    }
  }

  getProductTypeDesc(finType: any): FinanceTypeInterface {
    let value = '';
    let viewValue = '';
    if ((typeof (finType) === 'string' && (finType === 'invoiceDiscounting' || finType === '1' || finType.includes('discounting')))
      || (typeof (finType) === 'number' && finType === ProductType.invoiceDiscounting)) {
      value = '1';
      viewValue = 'Invoice Discounting';
    } else if ((typeof (finType) === 'string' && (finType === 'reverseFactoringSeller' || finType === '9' || (finType.includes('reverse') && finType.includes('seller'))))
      || (typeof (finType) === 'number' && finType === ProductType.reverseFactoringSeller)) {
      value = '9';
      viewValue = 'Reverse Factoring - Charges Borne by Seller';
    } else if ((typeof (finType) === 'string' && (finType === 'reverseFactoring' || finType === '3' || finType.includes('reverse')))
      || (typeof (finType) === 'number' && finType === ProductType.reverseFactoring)) {
      value = '3';
      viewValue = 'Reverse Factoring';
    } else if ((typeof (finType) === 'string' && (finType === 'tradePayablesFinancing' || finType === '4' || finType.includes('payables')))
      || (typeof (finType) === 'number' && finType === ProductType.tradePayablesFinancing)) {
      value = '4';
      viewValue = 'Trade Payables Financing';
    } else if ((typeof (finType) === 'string' && (finType === 'inventoryFinanceBuyer' || finType === '6' || finType.includes('inventory') && finType.includes('buyer')))
      || (typeof (finType) === 'number' && finType === ProductType.inventoryFinanceBuyer)) {
      value = '6';
      viewValue = 'Inventory Finance Buyer';
    } else if ((typeof (finType) === 'string' && (finType === 'inventoryFinanceBuyer' || finType === '7' || finType.includes('inventory') && finType.includes('seller')))
      || (typeof (finType) === 'number' && finType === ProductType.inventoryFinanceSeller)) {
      value = '7';
      viewValue = 'Inventory Finance Seller';
    } else if ((typeof (finType) === 'string' && (finType === 'revolvingCreditFacility' || finType === '8' || finType.includes('revolving') && finType.includes('facility')))
      || (typeof (finType) === 'number' && finType === ProductType.revolvingCreditFacility)) {
      value = '8';
      viewValue = 'Revolving Credit Facility';
    } else if ((typeof (finType) === 'string' && (finType === 'invoiceFactoringWoRecourse' || finType === '5' || finType.includes('factoring') && finType.includes('recourse')))
      || (typeof (finType) === 'number' && finType === ProductType.invoiceFactoringWoRecourse)) {
      value = '5';
      viewValue = 'Invoice Factoring w/o Recourse';
    } else if ((typeof (finType) === 'string' && (finType === 'mrpaClient' || finType === '11' || (finType.includes('mrpa') && finType.includes('client'))))
      || (typeof (finType) === 'number' && finType === ProductType.mrpaClient)) {
      value = '11';
      viewValue = 'MRPA - Disbursement to Client';
    } else if ((typeof (finType) === 'string' && (finType === 'mrpa' || finType === '10' || finType.includes('mrpa')))
      || (typeof (finType) === 'number' && finType === ProductType.mrpa)) {
      value = '10';
      viewValue = 'MRPA';
    } else if ((typeof (finType) === 'string' && (finType === 'scfDpwfs' || finType === '12' || (finType.includes('scf') && finType.includes('dpwfs'))))
      || (typeof (finType) === 'number' && finType === ProductType.scfDpwfs)) {
      value = '12';
      viewValue = 'SCF by DPWFS';
    } else if ((typeof (finType) === 'string' && (finType === 'advancePaymentSolutionClient' || finType === '13' || (finType.includes('advance') && finType.includes('payment') && finType.includes('client'))))
      || (typeof (finType) === 'number' && finType === ProductType.advancePaymentSolutionClient)) {
      value = '13';
      viewValue = 'Advance Payment Solution – Disbursement to Client';
    } else if ((typeof (finType) === 'string' && (
      finType === 'advancePaymentSolutionCounterParty'
      || finType === '14'
      || (finType.includes('advance') && finType.includes('payment') && (finType.includes('cp') || finType.includes('counter')))
    ))
      || (typeof (finType) === 'number' && finType === ProductType.advancePaymentSolutionCounterParty)) {
      value = '14';
      viewValue = 'Advance Payment Solution – Disbursement to Counter Party';
    } else {
      value = '2';
      viewValue = 'Invoice Factoring'
    }
    return { value, viewValue };
  }

  // LC stands for lower case
  getProductTypeAbbreviationSnakeLC(value: string | number): string {
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    const prodTypeSnakeUC = this.getProductTypeAbbreviationSnakeUC(value);
    return prodTypeSnakeUC.toLowerCase();
  }

  // LC stands for lower case
  getProductTypeFirstLetterCapital(value: string | number): string {
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    const prodTypeSnakeUC = this.getProductTypeAbbreviationSnakeUC(value);
    return this.firstLetterCaptial(prodTypeSnakeUC, '_');
  }

  // CC stands for camel case => 'InvoiceFactoring'
  getProductTypeFirstLetterCapCC(value: string): string {
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    const firstLetterCap = this.getProductTypeFirstLetterCapital(value);
    return firstLetterCap.replace(/_/g, '');
  }

  getProductTypeCamelCase(value): string {
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    const productTypeSnackUC = this.getProductTypeAbbreviationSnakeUC(value);
    return this.convertToCamelCase(productTypeSnackUC);
  }


  getProductTypeEnum(value: string | number): number {
    const prodTypeSnakeUC = this.getProductTypeAbbreviationSnakeUC(value);
    switch (prodTypeSnakeUC) {
      case 'INVOICE_DISCOUNTING': {
        return ProductType.invoiceDiscounting;
      }
      case 'INVOICE_FACTORING': {
        return ProductType.invoiceFactoring;
      }
      case 'REVERSE_FACTORING': {
        return ProductType.reverseFactoring;
      }
      case 'TRADE_PAYABLES_FINANCING': {
        return ProductType.tradePayablesFinancing;
      }
      case 'INVOICE_FACTORING_WO_RECOURSE': {
        return ProductType.invoiceFactoringWoRecourse;
      }
      case 'INVENTORY_FINANCE_BUYER': {
        return ProductType.inventoryFinanceBuyer;
      }
      case 'INVENTORY_FINANCE_SELLER': {
        return ProductType.inventoryFinanceSeller;
      }
      case 'REVOLVING_CREDIT_FACILITY': {
        return ProductType.revolvingCreditFacility;
      }
      case 'REVERSE_FACTORING_SELLER': {
        return ProductType.reverseFactoringSeller
      }
      case 'MRPA': {
        return ProductType.mrpa
      }
      case 'MRPA_CLIENT': {
        return ProductType.mrpaClient
      }
      case 'SCF_DPWFS': {
        return ProductType.scfDpwfs
      }
      case 'ADVANCE_PAYMENT_SOLUTION_CLIENT': {
        return ProductType.advancePaymentSolutionClient
      }
      case 'ADVANCE_PAYMENT_SOLUTION_CP': {
        return ProductType.advancePaymentSolutionCounterParty;
      }
      default: ''
    }
  }

  convertToCamelCase(value: string): string {
    if (!value) {
      return '';
    }
    return value.toLowerCase().replace(/_(.)/g, (match, firstLetter) => {
      return firstLetter.toUpperCase();
    });
  }

  validateProductControl(
    productCategory: string,
    productType: string,
    sectionKey: string,
    validationKey: string,
    rootFieldKey?: string,
    ...childFieldKeys: string[]
  ): boolean {
    if (!productCategory || !productType || !sectionKey) {
      return false;
    }

    const getConfigValue = (config: SectionConfig, keys: string[]): boolean => {
      let currentConfig: any = config;
      for (const key of keys) {
        if (!currentConfig.fieldData[key]) {
          return false;
        }
        currentConfig = currentConfig.fieldData[key];
      }

      return currentConfig[validationKey] || false;
    };

    const config = this.productConfig?.[productCategory]?.[productType]?.[sectionKey];

    if (!config) {
      return false;
    }

    if (rootFieldKey) {
      const keys = childFieldKeys.length > 0 ? [rootFieldKey, ...childFieldKeys] : [rootFieldKey];
      return getConfigValue(config, keys);
    } else {
      return config?.[validationKey] || false;
    }
  }

  getProdConfigValueForKey(
    productCategory: string,
    productType: string,
    sectionKey: string,
    returnKey: string,
    rootFieldKey?: string,
    childFieldKey_1?: string
  ): string {
    if (!productCategory || !productType || !sectionKey || !returnKey) {
      return 'false';
    }

    const getConfigValue = (config: SectionConfig, keys: string[]): string => {
      let currentConfig: any = config;
      for (const key of keys) {
        if (!currentConfig.fieldData[key]) {
          return '';
        }
        currentConfig = currentConfig.fieldData[key];
      }

      return currentConfig[returnKey] || '';
    };

    const config = this.productConfig?.[productCategory]?.[productType]?.[sectionKey];

    if (!config) {
      return '';
    }

    if (rootFieldKey) {
      const keys = childFieldKey_1 ? [rootFieldKey, childFieldKey_1] : [rootFieldKey];
      return getConfigValue(config, keys);
    } else {
      return config?.[returnKey] || '';
    }
  }

  getProductCategoryInTitleCase(category: string, productType?: string): string {
    if (category === '' || category === null || category === undefined) {
      return '';
    }
    category = category.toLowerCase();
    while (category.includes('_')) {
      category = category.replace('_', '');
    }
    if (productType) {
      const productTypeSnackUC = this.getProductTypeAbbreviationSnakeUC(productType);
      if (!this.getGivenKeyValueForCreditFacilityConfig(productTypeSnackUC, 'commonConfig', 'isCategoryEnabled')) {
        return ''
      }
    }
    if (category.includes('logistics')) {
      return 'Logistics Finance';
    } else {
      return 'Trade Finance';
    }
  }

  getCategoryInTitleCaseForNum(category: number): string {
    return category === 0 ? 'Trade Finance' : 'Logistics Finance';
  }

  getFinanceTypeInCapital(financeType: string): string {
    financeType = financeType.toUpperCase();
    if (financeType.includes('DEFERRED')) return 'DEFERRED';
    else return 'IN_TRANSIT';
  }

  private isDisbursementAndCollectionValid(categorySnakeUC: string, productTypeSnakeUc: string, disbursement: string, collection: string): boolean {
    if (!categorySnakeUC) {
      categorySnakeUC = 'TRADE_FINANCE';
    }
    if (!productTypeSnakeUc) {
      return false;
    }

    const disbursementTo = this.getProdConfigValueForKey(categorySnakeUC, productTypeSnakeUc, 'DISBURSEMENT_TO', 'label');
    const collectedFrom = this.getProdConfigValueForKey(categorySnakeUC, productTypeSnakeUc, 'COLLECTION_FROM', 'label');

    return (disbursementTo === disbursement && collectedFrom === collection);
  }

  isSponsorInvolved(categorySnakeUC: string, productTypeSnakeUc: string): boolean {
    return this.getProdConfigValueForKey(categorySnakeUC, productTypeSnakeUc, 'DISBURSEMENT_TO', 'label') === 'SPONSOR' || this.getProdConfigValueForKey(categorySnakeUC, productTypeSnakeUc, 'COLLECTION_FROM', 'label') === 'SPONSOR';
  }

  // in mrpa cp becomes applicant and applicant becomes sponsor
  isDisbursementToCpAndCollectedFromApplicant(categorySnakeUC: string, productTypeSnakeUc: string): boolean {
    return this.isDisbursementAndCollectionValid(categorySnakeUC, productTypeSnakeUc, 'COUNTER_PARTY', 'APPLICANT') || this.isDisbursementToApplicantAndCollectedFromSponsor(categorySnakeUC, productTypeSnakeUc);
  }

  // mrpa client case
  isDisbursementToApplicantAndCollectedFromSponsor(categorySnakeUC: string, productTypeSnakeUc: string): boolean {
    return this.isDisbursementAndCollectionValid(categorySnakeUC, productTypeSnakeUc, 'APPLICANT', 'SPONSOR');
  }

  isDisbursementToApplicantAndCollectedFromApplicant(categorySnakeUC: string, productTypeSnakeUc: string): boolean {
    return this.isDisbursementAndCollectionValid(categorySnakeUC, productTypeSnakeUc, 'APPLICANT', 'APPLICANT') || this.isDisbursementToSponsorAndCollectedFromSponsor(categorySnakeUC, productTypeSnakeUc);
  }

  // mrpa case
  isDisbursementToSponsorAndCollectedFromSponsor(categorySnakeUC: string, productTypeSnakeUc: string): boolean {
    return this.isDisbursementAndCollectionValid(categorySnakeUC, productTypeSnakeUc, 'SPONSOR', 'SPONSOR');
  }

  isDisbursementToApplicantAndCollectedFromCp(categorySnakeUC: string, productTypeSnakeUc: string): boolean {
    return this.isDisbursementAndCollectionValid(categorySnakeUC, productTypeSnakeUc, 'APPLICANT', 'COUNTER_PARTY');
  }

  // the old funtion was explictly used for places where factoring fees was requried
  // since now the applicnt to CP and other conditions can also be different and recourseLabel 
  // is no longer a valid way to check have renamed it to isfactoringFeeApplicable 
  // and added the appropropirate new conditions
  isfactoringFeeApplicable(categorySnakeUC: string, productTypeSnakeUc: string): boolean {
    if (categorySnakeUC) {
      return productTypeSnakeUc === 'INVOICE_FACTORING_WO_RECOURSE' || productTypeSnakeUc === 'REVERSE_FACTORING_SELLER';
    }
    return false;
  }

  getFinanceTypeInTitleCase(financeType: string): string {
    return this.firstLetterCaptial(this.getFinanceTypeInCapital(financeType), '_');
  }

  // for RFS product which is similar to RF product, now it has settlment by FI step, current implementation depend on applicantToCp flag so
  //  for RFS we have to make this flag as true for settlmenet step only
  isSettlementByFiApplicable(productCategory: string, productType: string): boolean {
    return !!this.validateProductControl(productCategory, productType, 'SETTLEMENT_BY_FI', 'isVisible');
  }

  getValidLabelForBaseDocument(
    productTypeSnakeUC: string,
    isForErrorMessageOrWithoutType?: boolean,
    selectedDocumentType?: string
  ): string {
    const baseDocumentTypeLabelMap = {
      purchase_order: 'Purchase Order',
      sales_contract: 'Sales Contract',
      proforma_invoice: 'Performa Invoice',
    };
    switch (productTypeSnakeUC) {
      case 'MRPA':
      case 'MRPA_CLIENT':
        return isForErrorMessageOrWithoutType ? '' : 'Application Contract';
      case 'SCF_DPWFS':
        return isForErrorMessageOrWithoutType ? '' : 'Underlying Document'
      case 'ADVANCE_PAYMENT_SOLUTION_CLIENT':
      case 'ADVANCE_PAYMENT_SOLUTION_CP':
        return baseDocumentTypeLabelMap[selectedDocumentType]
          ? baseDocumentTypeLabelMap[selectedDocumentType]
          : (isForErrorMessageOrWithoutType ? '' : 'Document');
      default:
        return 'Invoice';
    }
  }

  fieldNameToDisplayNameObject = {
    infostep: [
      {
        formControlName: 'invoiceCurrency',
        label: 'Currency'
      },
      {
        formControlName: 'invoiceDetails',
        label: 'Details'
      },
      {
        formControlName: 'invoiceAmountInCurrency',
        label: 'Amount in Currency'
      },
      {
        formControlName: 'invoiceAmount',
        label: 'Amount'
      },
      {
        formControlName: 'invoiceTerms',
        label: 'Terms'
      },
      {
        formControlName: 'invoiceId',
        label: 'Id'
      },
      {
        formControlName: 'invoiceDueDate',
        label: 'Due Date'
      },
      {
        formControlName: 'invoiceDate',
        label: 'Date'
      },
    ],
    bankAssessment: [
      {
        formControlName: 'invoiceAmount',
        label: 'Amount',
      },
      {
        formControlName: 'invoiceCurrency',
        label: 'Currency'
      }
    ]
  }

  getProductTypeInTitleCase(productType: string): string {
    let prodTypeSnakeUC = this.getProductTypeAbbreviationSnakeUC(productType);
    switch (prodTypeSnakeUC) {
      case 'INVOICE_DISCOUNTING':
        return 'Invoice Discounting';
      case 'INVOICE_FACTORING':
        return 'Invoice Factoring';
      case 'REVERSE_FACTORING':
        return 'Reverse Factoring';
      case 'TRADE_PAYABLES_FINANCING':
        return 'Trade Payables Financing';
      case 'INVOICE_FACTORING_WO_RECOURSE':
        return 'Invoice Factoring w/o Recourse';
      case 'INVENTORY_FINANCE_BUYER':
        return 'Inventory Finance Buyer';
      case 'INVENTORY_FINANCE_SELLER':
        return 'Inventory Finance Seller';
      case 'REVOLVING_CREDIT_FACILITY':
        return 'Revolving Credit Facility';
      case 'REVERSE_FACTORING_SELLER':
        return 'Reverse Factoring - Charges Borne by Seller';
      case 'MRPA':
        return 'MRPA';
      case 'MRPA_CLIENT':
        return 'MRPA - Disbursement to Client';
      case 'SCF_DPWFS':
        return 'SCF by DPWFS';
      case 'ADVANCE_PAYMENT_SOLUTION_CLIENT':
        return 'Advance Payment Solution – Disbursement to Client';
      case 'ADVANCE_PAYMENT_SOLUTION_CP':
        return 'Advance Payment Solution – Disbursement to Counter Party';
      default:
        return '';
    }
  }

  findFinancesIndex(productCategory: string, productType: string): number {
    const index = this.productOnboardingConfig.products.findIndex((instance, index) => {
      if (productCategory === 'SCF' && instance.apiKey === 'SUPPLY_CHAIN_FINANCING') {
        return true;
      } else {
        const productKey = productCategory + '_' + productType;
        if (instance.apiKey.includes(productKey)) {
          return instance.apiKey.includes(productKey);
        }
      }
    });
    return index;
  }

  getProductFinanceDesc(item): string {
    if (item.includes('_FINANCE')) {
      const productType = this.getProductTypeInTitleCase(item);
      const category = this.getProductCategoryInTitleCase(item, productType);
      let financeTypePresent = this.getAllFinanceTypeList().some(singleFinanceType => item.includes(singleFinanceType));
      if (financeTypePresent) {
        const financeType = this.getFinanceTypeInTitleCase(item);
        return category ? (category + ': ' + productType + ': ' + financeType) : productType + ': ' + financeType;
      }
      return category ? (category + ': ' + productType) : productType;
    }
    return this.getFinanceTypeInTitleCase(item);
  }

  getBuyerOrImporterAsLabel(category: string): string {
    return this.conditionMapConfig.productCategory[category].buyerImporterLabel;
  }

  getProductDisplayName(productCategory: string, productType: string): string {
    productCategory = this.getProductCategoryInTitleCase(productCategory, productType);
    let viewValue = this.getProductTypeInTitleCase(productType);
    if (productCategory) {
      viewValue = productCategory + ': ' + viewValue;
    }
    return viewValue;
  }

  isEffectiveFullCounterPartyForm(productTypeInput: string, allowAdvancePayment?: boolean): boolean {
    const productType = this.getProductTypeAbbreviationSnakeUC(productTypeInput);
    const configuredFormType = this.getGivenKeyValueForCreditFacilityConfig(
      productType,
      'counterPartyComponentsConfig',
      'counterPartyFormType'
    );
    if (configuredFormType === counterPartyFormTypeENUM.FULL) {
      return true;
    }
    return (productType === 'INVOICE_DISCOUNTING' || productType === 'MRPA_CLIENT') && !!allowAdvancePayment;
  }

  isEffectedApsFullCounterPartyForm(productTypeInput: string, emitChangeInType: boolean): boolean {
    const productType = this.getProductTypeAbbreviationSnakeUC(productTypeInput);
    const configuredFormType = this.getGivenKeyValueForCreditFacilityConfig(
      productType,
      'counterPartyComponentsConfig',
      'apsCounterPartyFormType'
    );
    if (emitChangeInType) {
      const configuredFormType2 = this.getGivenKeyValueForCreditFacilityConfig(
        productType,
        'counterPartyComponentsConfig',
        'counterPartyFormType'
      );
      return configuredFormType !== configuredFormType2;
    }
    return configuredFormType === counterPartyFormTypeENUM.FULL;
  }

  // return true when has invalid combination selected
  // clubs -> MRPA + MRPA_CLIENT // RCF // Rest Products
  checkFacilityProductCompatability(productCombination: any[]): boolean {
    let mrpaProductClubCount = 0;
    let rcfProductClubCount = 0;
    const selectedProductsCount = productCombination.length;
    for (let i = 0; i < selectedProductsCount; i++) {
      const key = productCombination[i];
      if (key.includes('MRPA')) mrpaProductClubCount++;
      if (key.includes('REVOLVING_CREDIT_FACILITY')) rcfProductClubCount++;
    }
    if (rcfProductClubCount && selectedProductsCount !== 1) return true;
    if (mrpaProductClubCount && selectedProductsCount !== mrpaProductClubCount) return true;
    return false;
  }

  checkProductAndFinanceTypeCompatability(products: Array<any>, financeTypes: Array<any>): boolean {
    const hasRCF = products.includes('REVOLVING_CREDIT_FACILITY');
    const hasInTransit = financeTypes.some(obj =>
      Object.values(obj).includes('IN_TRANSIT')
    );
    return hasRCF && hasInTransit;
  }

  checkProductsIncludeRCF(products): boolean {
    return products?.filter(element => {
      return element.includes('REVOLVING_CREDIT_FACILITY');
    })?.length ? true : false;
  }

  inventoryFinanceProduct(productTypeSnakeUC): boolean {
    return productTypeSnakeUC.includes('INVENTORY_FINANCE') ? true : false;
  }

  validatePOCombination(selectedFinanceTypes: any[]): { isValid: boolean, message?: string } {
    const apClientMarker = 'ADVANCE_PAYMENT_SOLUTION_CLIENT';
    const hasAPClient = selectedFinanceTypes.some(
      item => typeof item === 'string' && item.includes(apClientMarker)
    );

    if (hasAPClient) {
      if (selectedFinanceTypes.length === 1) {
        return { isValid: false, message: 'Advance Payment Solution – Disbursement to Client cannot be selected alone. It should be combined with Inventory, Discount, or Factoring' };
      }

      const hasCompatibleProduct = selectedFinanceTypes.some(
        item =>
          typeof item === 'string' &&
          !item.includes(apClientMarker) &&
          ALLOWED_ADVANCE_PAYMENT_SOLUTION_CLIENT_PRODUCTS.some(allowed => item.includes(allowed))
      );

      if (!hasCompatibleProduct) {
        return {
          isValid: false,
          message: 'Advance Payment Solution – Disbursement to Client must be combined with at least one of Inventory, Discount, or Factoring.'
        };
      }
    }

    return { isValid: true };
  }

  validateBuyerLedPOCombination(selectedFinanceTypes: any[]): { isValid: boolean, message?: string } {
    const tradeBuyerLedPo = 'TRADE_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP';
    const logisticsBuyerLedPo = 'LOGISTICS_FINANCE_ADVANCE_PAYMENT_SOLUTION_CP';
    const tradeTradePayable = 'TRADE_FINANCE_TRADE_PAYABLE_FINANCING';
    const tradeTradePayables = 'TRADE_FINANCE_TRADE_PAYABLES_FINANCING';
    const logisticsTradePayable = 'LOGISTICS_FINANCE_TRADE_PAYABLE_FINANCING';
    const logisticsTradePayables = 'LOGISTICS_FINANCE_TRADE_PAYABLES_FINANCING';
    const financeTypes = Array.isArray(selectedFinanceTypes) ? selectedFinanceTypes : [];

    const hasTradeBuyerLedPo = financeTypes.some(item => typeof item === 'string' && item.includes(tradeBuyerLedPo));
    const hasLogisticsBuyerLedPo = financeTypes.some(item => typeof item === 'string' && item.includes(logisticsBuyerLedPo));
    const hasTradeTradePayables = financeTypes.some(item =>
      typeof item === 'string' && (item.includes(tradeTradePayable) || item.includes(tradeTradePayables))
    );
    const hasLogisticsTradePayables = financeTypes.some(item =>
      typeof item === 'string' && (item.includes(logisticsTradePayable) || item.includes(logisticsTradePayables))
    );

    if (hasTradeBuyerLedPo && !hasTradeTradePayables) {
      return {
        isValid: false,
        message: 'Trade Finance Advance Payment Solution – Disbursement to Counter Party must be combined with Trade Finance :Trade Payables Financing.'
      };
    }

    if (hasLogisticsBuyerLedPo && !hasLogisticsTradePayables) {
      return {
        isValid: false,
        message: 'Logistics Finance Advance Payment Solution – Disbursement to Counter Party must be combined with Logistics Finance :Trade Payables Financing.'
      };
    }

    return { isValid: true };
  }

  isAdvancePaymentSolutionClient(productType: string): boolean {
    return this.getProductTypeDesc(productType.toLowerCase()).value === this.getProductTypeDesc(ProductType.advancePaymentSolutionClient).value;
  }

  isAdvancePaymentSolutionCounterParty(productType: string): boolean {
    return this.getProductTypeDesc(productType.toLowerCase()).value === this.getProductTypeDesc(ProductType.advancePaymentSolutionCounterParty).value;
  }

  isAdvancePaymentSolutionProduct(productTypeInput: string): boolean {
    const productType = this.getProductTypeAbbreviationSnakeUC(productTypeInput);
    return productType === 'ADVANCE_PAYMENT_SOLUTION_CLIENT'
      || productType === 'ADVANCE_PAYMENT_SOLUTION_CP';
  }

  getAllowedAdvancePaymentClientProducts() {
    return ALLOWED_ADVANCE_PAYMENT_SOLUTION_CLIENT_PRODUCTS;
  }

  getAllowedAdvancePaymentCounterPartyProducts() {
    return ALLOWED_ADVANCE_PAYMENT_SOLUTION_COUNTERPARTY_PRODUCTS;
  }

  checkPoCompatibleProduct(productType: any): boolean {
    const allowedWithPO = [
      'INVOICE_DISCOUNTING',
      'INVOICE_FACTORING',
      'INVENTORY_FINANCE_SELLER',
      'TRADE_PAYABLES_FINANCING'
    ];
    return allowedWithPO.includes(productType);
  }

  getTargetAPSProduct(sourceCategorySnakeUc: string, sourceProductTypeSnakeUc: string): string {
    const disbursementToLabel = this.getProdConfigValueForKey(
      sourceCategorySnakeUc,
      sourceProductTypeSnakeUc,
      'DISBURSEMENT_TO',
      'label'
    );
    return disbursementToLabel === 'COUNTER_PARTY' ? 'ADVANCE_PAYMENT_SOLUTION_CP' : 'ADVANCE_PAYMENT_SOLUTION_CLIENT';
  }
}
